import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  ChevronRight, Calendar, Clock, Users, ArrowLeft, Trophy, 
  LogIn, LogOut, User, Plus, FileText, AlertCircle, X, Download,
  CheckCircle, Edit2, Trash2, GripVertical, Shield, Settings, FileDown, Save, Banknote
} from "lucide-react";
import { Button } from "./components/ui/button";
import { AuthModal } from "./components/AuthModal";
import { ProfileMenu } from "./components/ProfileMenu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

// LAUSZ szerinti versenykategóriák
const CATEGORY_ORDER = [
  "Díjlovaglás",
  "Ügyességi", 
  "Díjugratás",
  "Futószáras",
  "Lovastusa",
  "Hobby Horse",
  "Lovastorna",
  "Caprilli",
  "Csikós",
  "Mounted Games",
  "Tájékozódási",
  "Show",
  "Working Equitation",
  "Távlovas"
];

// LAUSZ szerinti elbírálási típusok részletes leírással
const JUDGING_TYPES = {
  "hiba_ido": "Hiba-idő",
  "alapido": "Alapidő", 
  "ket_fazis": "Két fázis",
  "dijlovaglas": "Díjlovaglás",
  "stilus": "Stílus",
  "pontgyujto": "Pontgyűjtő",
  "vadaszugratas": "Vadászugratás (C-szabály)",
  "hunter": "LAUSZ Hunter",
  "fokozatos": "Fokozatos nehézségű",
  "idealis_ido": "Ideális időre",
  "ketfordulos": "Kétfordulós",
  "caprilli": "Caprilli bírálat",
  "tajekozdasi": "Tájékozódási bírálat"
};

// Elbírálási típusok rövid leírása (tooltip-hez)
const JUDGING_DESCRIPTIONS = {
  "hiba_ido": "Hibapont + idő alapján. Legkevesebb hiba, azonos hibánál gyorsabb idő nyer.",
  "alapido": "Meghatározott alapidő, túllépésnél +1 hibapont/megkezdett 2 mp.",
  "ket_fazis": "I. fázis hibátlan → II. fázis (összevetés). Csak hibátlan I. fázis után folytatható.",
  "dijlovaglas": "Feladatok pontozása, százalékos eredmény. Legmagasabb % nyer.",
  "stilus": "Ló-lovas harmónia, ülés, segítségadás értékelése pontokkal.",
  "pontgyujto": "Akadályokért pontok járnak, legtöbb pont nyer.",
  "vadaszugratas": "C-szabály: hibák másodpercekké válnak, összidő dönt.",
  "hunter": "LAUSZ Hunter szabályok szerint (stílus + akadályteljesítés).",
  "fokozatos": "Akadályok nehézsége fokozatosan nő, egyre több pontot érnek.",
  "idealis_ido": "Előre megadott ideális időhöz legközelebbi idő nyer.",
  "ketfordulos": "Két forduló, első hibátlan → második forduló dönt.",
  "caprilli": "Ló idomítottsága, lovas ülése, engedelmesség pontozása.",
  "tajekozdasi": "Útvonal követése térképpel, pontosság és idő alapján."
};

// LAUSZ szerinti hivatalos személyek szerepei
const ROLES = [
  { id: "vezetobiro", name: "Vezetőbíró" },
  { id: "biro", name: "Bíró" },
  { id: "stilusbiro", name: "Stílusbíró" },
  { id: "dijlovasbiro", name: "Díjlovas bíró" },
  { id: "lovastusabiro", name: "Lovastusa bíró" },
  { id: "melegitobiro", name: "Melegítőpálya bíró" },
  { id: "versenyszervezo", name: "Versenyszervező" },
  { id: "palyaepito", name: "Pályaépítő" },
  { id: "versenyiroda", name: "Versenyiroda" }
];

export default function Competition() {
  const { competitionId: compParam, eventId: evParam } = useParams();
  const navigate = useNavigate();
  const isUuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v || "");
  const routeKey = `${compParam || ""}/${evParam || ""}`;
  const [resolvedRoute, setResolvedRoute] = useState({ key: null, competitionId: null, eventId: null });
  useEffect(() => {
    if (!compParam) { setResolvedRoute({ key: routeKey, competitionId: null, eventId: null }); return; }
    if (isUuid(compParam) && (!evParam || isUuid(evParam))) { setResolvedRoute({ key: routeKey, competitionId: compParam, eventId: evParam || null }); return; }
    let alive = true;
    axios.get(`${API}/competitions/resolve/${encodeURIComponent(compParam)}${evParam ? `/${encodeURIComponent(evParam)}` : ""}`)
      .then((r) => { if (alive) setResolvedRoute({ key: routeKey, competitionId: r.data.competition_id, eventId: r.data.event_id || null }); })
      .catch(() => { if (alive) navigate("/verseny", { replace: true }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);
  const competitionId = resolvedRoute.key === routeKey ? resolvedRoute.competitionId : null;
  const eventId = resolvedRoute.key === routeKey ? resolvedRoute.eventId : null;
  const compPath = (c) => `/verseny/${c?.slug || c?.id || ""}`;
  const eventPath = (c, ev) => `${compPath(c)}/${ev?.slug || ev?.id || ""}`;

  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem("comp_token");
    return (stored && stored !== "undefined" && stored !== "null") ? stored : "";
  });
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Data state
  const [competitions, setCompetitions] = useState([]);
  const [competition, setCompetition] = useState(null);
  useEffect(() => {
    if (!competition || !competitionId || competition.id !== competitionId) return;
    const ev = eventId ? (competition.events || []).find((e) => e.id === eventId) : null;
    if (eventId && !ev) return;
    const canonical = ev ? eventPath(competition, ev) : compPath(competition);
    if (decodeURIComponent(window.location.pathname) !== canonical) navigate(canonical, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition, competitionId, eventId]);
  const [event, setEvent] = useState(null);
  const [startlist, setStartlist] = useState([]);
  const [horses, setHorses] = useState([]);
  const [allHorses, setAllHorses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [roleUsers, setRoleUsers] = useState({ 
    vezetobiro: [], judges: [], stilusbiro: [], dijlovasbiro: [], 
    lovastusabiro: [], melegitobiro: [], organizers: [], 
    course_builders: [], versenyiroda: [] 
  });
  const [linkedAccounts, setLinkedAccounts] = useState([]); // Hozzárendelt fiókok
  const [loading, setLoading] = useState(true);

  // UI state
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedHorseId, setSelectedHorseId] = useState("");
  const [selectedRiderId, setSelectedRiderId] = useState("");
  
  // Tab state for competition list view
  const [activeTab, setActiveTab] = useState("versenyek"); // versenyek, ranglista, lovas-lo, elmult
  
  const [createForm, setCreateForm] = useState({
    name: "", date: "", start_time: "09:00", description: "", entry_deadline: "",
    director_ids: [], judge_ids: [], course_builder_ids: [],
    entry_fee_enabled: false, entry_fee_tier1_limit: 2, entry_fee_tier1_price: 3000, entry_fee_tier2_price: 2500
  });
  const [editForm, setEditForm] = useState(null);
  const [eventForms, setEventForms] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });
  const [entryFeeCalc, setEntryFeeCalc] = useState(null); // For showing entry fee in modal

  // Scoring & Entry management state
  const [scoringMode, setScoringMode] = useState(false);
  const [entryManagementMode, setEntryManagementMode] = useState(false);
  const [showStartTimeModal, setShowStartTimeModal] = useState(false);
  const [showCourseMapModal, setShowCourseMapModal] = useState(false);
  const [courseMapUploading, setCourseMapUploading] = useState(false);
  const [resultsData, setResultsData] = useState({});
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [eventStartTime, setEventStartTime] = useState("");  // The actual start time of the event
  const [submittingForApproval, setSubmittingForApproval] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null); // {pending, approved, approved_by, rejected_by}

  // Participants modal state
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [participantsView, setParticipantsView] = useState("select"); // "select", "riders", "horses"
  const [selectedParticipant, setSelectedParticipant] = useState(null); // { type: "rider"|"horse", id, name }
  const [participantsData, setParticipantsData] = useState({ riders: [], horses: [] });

  // Payments modal state
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [paymentsData, setPaymentsData] = useState({ payments: [], entry_fee_enabled: false });
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [savingPayment, setSavingPayment] = useState(null);

  // ==================== DATA FETCHING ====================
  const fetchUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/me?token=${token}`);
      setUser(res.data);
    } catch {
      localStorage.removeItem("comp_token");
      setToken("");
      setUser(null);
    }
  }, [token]);

  const fetchCompetitions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/competitions`);
      const sorted = res.data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setCompetitions(sorted);
    } catch (err) {
      console.error("Failed to fetch competitions", err);
    }
  }, []);

  const fetchCompetition = useCallback(async (id) => {
    try {
      // Include token to get user permissions
      const params = token ? { token } : {};
      const res = await axios.get(`${API}/competitions/${id}`, { params });
      setCompetition(res.data);
    } catch (err) {
      console.error("Failed to fetch competition", err);
    }
  }, [token]);

  // Fetch all participants (riders and horses) for a competition
  const fetchParticipants = useCallback(async (compId) => {
    try {
      const res = await axios.get(`${API}/competitions/${compId}`);
      const comp = res.data;
      
      // Collect all entries from all events
      const allEntries = [];
      for (const ev of (comp.events || [])) {
        const startlistRes = await axios.get(`${API}/competition/startlist/${compId}/${ev.id}`);
        const entries = startlistRes.data.entries || [];
        entries.forEach(entry => {
          // Use rider_name if available, fallback to user_name
          const userName = entry.rider_name || entry.user_name || "";
          allEntries.push({
            ...entry,
            user_name: userName,
            event_id: ev.id,
            event_name: ev.name,
            event_number: ev.number || 0
          });
        });
      }
      
      // Group by riders
      const ridersMap = {};
      allEntries.forEach(entry => {
        const riderId = entry.rider_id || entry.user_id;
        const riderName = entry.user_name;
        if (riderId && !ridersMap[riderId]) {
          ridersMap[riderId] = { id: riderId, name: riderName, entries: [] };
        }
        if (riderId) {
          ridersMap[riderId].entries.push(entry);
        }
      });
      
      // Group by horses
      const horsesMap = {};
      allEntries.forEach(entry => {
        const horseId = entry.horse_id;
        const horseName = entry.horse_name;
        if (horseId && !horsesMap[horseId]) {
          horsesMap[horseId] = { id: horseId, name: horseName, entries: [] };
        }
        if (horseId) {
          horsesMap[horseId].entries.push(entry);
        }
      });
      
      // Convert to sorted arrays (ABC order)
      const riders = Object.values(ridersMap).sort((a, b) => 
        (a.name || "").localeCompare(b.name || "", 'hu')
      );
      const horses = Object.values(horsesMap).sort((a, b) => 
        (a.name || "").localeCompare(b.name || "", 'hu')
      );
      
      setParticipantsData({ riders, horses });
    } catch (err) {
      console.error("Failed to fetch participants", err);
      setParticipantsData({ riders: [], horses: [] });
    }
  }, []);

  // Fetch payments data for a competition
  const fetchPayments = useCallback(async (compId) => {
    if (!token) return;
    setPaymentsLoading(true);
    try {
      const res = await axios.get(`${API}/competition/${compId}/payments?token=${token}`);
      setPaymentsData(res.data);
    } catch (err) {
      console.error("Failed to fetch payments", err);
      if (err.response?.status === 403) {
        toast.error("Nincs jogosultságod a fizetések megtekintéséhez");
      }
      setPaymentsData({ payments: [], entry_fee_enabled: false });
    } finally {
      setPaymentsLoading(false);
    }
  }, [token]);

  // Update payment for a rider
  const updatePayment = async (riderId, amountPaid) => {
    if (!token || !competitionId) return;
    setSavingPayment(riderId);
    try {
      await axios.put(`${API}/competition/${competitionId}/payments/${riderId}?token=${token}`, {
        amount_paid: parseInt(amountPaid) || 0
      });
      // Refresh payments data
      await fetchPayments(competitionId);
    } catch (err) {
      console.error("Failed to update payment", err);
      toast.error("Hiba a fizetés mentésekor");
    } finally {
      setSavingPayment(null);
    }
  };

  const fetchStartlist = useCallback(async (compId, evtId) => {
    try {
      const res = await axios.get(`${API}/competition/startlist/${compId}/${evtId}`);
      setStartlist(res.data.entries || []);
      setEvent(res.data.event || null);
      
      const initialResults = {};
      (res.data.entries || []).forEach(entry => {
        initialResults[entry.id] = {
          time_seconds: entry.time_seconds || "",
          penalty_points: entry.penalty_points || "",
          style_points: entry.style_points || "",
          dressage_points: entry.dressage_points || "",
          phase1_penalty: entry.phase1_penalty ?? "",
          phase1_time: entry.phase1_time || "",
          phase1_eliminated: entry.phase1_eliminated || false,
          phase1_dnf: entry.phase1_dnf || false,
          phase2_penalty: entry.phase2_penalty ?? "",
          phase2_time: entry.phase2_time || "",
          phase2_eliminated: entry.phase2_eliminated || false,
          phase2_dnf: entry.phase2_dnf || false,
          status: entry.is_disqualified ? "kizarva" : entry.did_not_finish ? "feladta" : ""
        };
      });
      setResultsData(initialResults);
    } catch (err) {
      console.error("Failed to fetch startlist", err);
      setStartlist([]);
    }
  }, []);

  const fetchHorses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/competition/horses`);
      setHorses(res.data);
    } catch (err) {
      console.error("Failed to fetch horses", err);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/users?token=${token}`);
      setAllUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  }, [token]);

  const fetchAllHorses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/competition/horses`);
      setAllHorses(res.data);
    } catch (err) {
      console.error("Failed to fetch all horses", err);
    }
  }, []);

  const fetchRoleUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/competition/users-by-role`);
      setRoleUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch role users", err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchUser();
      await fetchHorses();
      await fetchAllHorses();
      await fetchRoleUsers();
      
      if (eventId && competitionId) {
        await fetchCompetition(competitionId);
        await fetchStartlist(competitionId, eventId);
      } else if (competitionId) {
        await fetchCompetition(competitionId);
      } else {
        await fetchCompetitions();
      }
      setLoading(false);
    };
    init();
  }, [competitionId, eventId, fetchUser, fetchCompetitions, fetchCompetition, fetchStartlist, fetchHorses, fetchAllHorses, fetchRoleUsers]);

  useEffect(() => {
    // Fetch all users for admin/judge to be able to enter others
    const shouldFetchUsers = user && eventId && (user.is_admin || isAssignedOfficial());
    if (shouldFetchUsers) {
      fetchAllUsers();
    }
    // Fetch linked accounts for all logged in users
    if (user && eventId) {
      fetchLinkedAccounts();
    }
  }, [user, competition, eventId, fetchAllUsers]);

  // Countdown timer
  useEffect(() => {
    const deadline = event?.entry_deadline || competition?.entry_deadline;
    if (!deadline) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const end = new Date(deadline);
      const diff = end - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds, expired: false });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [event, competition]);

  // ==================== AUTH HANDLERS ====================
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/competition/logout?token=${token}`);
    } catch (err) {
      // Ignore errors on logout
    }
    localStorage.removeItem("comp_token");
    setToken("");
    setUser(null);
  };

  // ==================== ROLE MANAGEMENT ====================
  const fetchLinkedAccounts = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/users/linked-accounts`, { params: { token } });
      setLinkedAccounts(res.data.linked_accounts || []);
    } catch (err) {
      console.error("Error fetching linked accounts:", err);
    }
  };

  const updateUserRole = async (userId, role, add) => {
    try {
      await axios.put(`${API}/competition/users/${userId}/roles`, {
        role, action: add ? "add" : "remove"
      }, { params: { token } });
      await fetchAllUsers();
    } catch (err) {
      alert("Hiba a jogosultság módosításakor");
    }
  };

  // ==================== ENTRY HANDLERS ====================
  const fetchEntryFeeCalc = async (riderId = null) => {
    if (!competitionId) return;
    try {
      const params = { token };
      if (riderId) params.user_id = riderId;
      if (eventId) params.event_id = eventId;
      const res = await axios.get(`${API}/competitions/${competitionId}/entry-fee-calculation`, { params });
      setEntryFeeCalc(res.data);
    } catch (err) {
      console.error("Failed to fetch entry fee calculation", err);
      setEntryFeeCalc(null);
    }
  };

  const openEntryModal = async () => {
    setShowEntryModal(true);
    await fetchEntryFeeCalc(selectedRiderId || null);
  };

  // LAUSZ: 18 év alatti lovas ellenőrzése
  const isRiderUnder18 = (riderId) => {
    const rider = riderId ? (allUsers.find(u => u.id === riderId) || linkedAccounts.find(la => la.id === riderId)) : user;
    if (!rider?.birth_date) return false;
    const birthDate = new Date(rider.birth_date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
    return actualAge < 18;
  };

  const handleEntry = async () => {
    if (!selectedHorseId) return;
    
    const riderId = selectedRiderId || user?.id;
    
    // Check max 3 entries per rider
    const riderEntries = startlist.filter(e => e.rider_id === riderId);
    if (riderEntries.length >= 3) {
      alert("Maximum 3 start lehet egy versenyszámban!");
      return;
    }
    
    try {
      await axios.post(`${API}/competition/entries`, {
        competition_id: competitionId,
        event_id: eventId,
        horse_id: selectedHorseId,
        rider_id: riderId
      }, { params: { token } });
      
      alert("A nevezés sikeresen rögzítve!");
      setShowEntryModal(false);
      setSelectedHorseId("");
      setSelectedRiderId("");
      await fetchStartlist(competitionId, eventId);
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba a nevezés során");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!confirm("Biztosan törölni szeretnéd a nevezést?")) return;
    try {
      await axios.delete(`${API}/competition/entries/${entryId}?token=${token}`);
      await fetchStartlist(competitionId, eventId);
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba a törlés során");
    }
  };

  const updateEntryHorse = async (entryId, newHorseId) => {
    try {
      await axios.put(`${API}/competition/entries/${entryId}/horse`, {
        horse_id: newHorseId
      }, { params: { token } });
      await fetchStartlist(competitionId, eventId);
    } catch (err) {
      alert("Hiba a ló módosításakor");
    }
  };

  const moveEntry = async (entryId, direction) => {
    const idx = startlist.findIndex(e => e.id === entryId);
    if (idx === -1) return;
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= startlist.length) return;
    
    const newList = [...startlist];
    [newList[idx], newList[newIdx]] = [newList[newIdx], newList[idx]];
    
    // Get ordered entry IDs
    const entry_ids = newList.map(entry => entry.id);
    
    try {
      await axios.put(`${API}/competition/entries/reorder/${eventId}`, {
        entry_ids
      }, { params: { token } });
      await fetchStartlist(competitionId, eventId);
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba a sorrend módosításakor");
    }
  };

  // ==================== SCORING HANDLERS ====================
  const isJudge = () => user?.roles?.includes("biro") || user?.is_admin;
  const isOrganizer = () => user?.roles?.includes("versenyszervezo") || user?.is_admin;
  
  // Check if user is assigned as an official to this specific competition
  const isAssignedOfficial = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    
    const userId = user.id;
    return (competition.director_ids || []).includes(userId) ||
           (competition.judge_ids || []).includes(userId) ||
           (competition.course_builder_ids || []).includes(userId);
  };
  
  // Check if user is ONLY an organizer (not admin, not judge, not course builder for this comp)
  const isOnlyOrganizer = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return false;
    
    const userId = user.id;
    const isDirector = (competition.director_ids || []).includes(userId);
    const isJudgeAssigned = (competition.judge_ids || []).includes(userId);
    const isCourseBuilder = (competition.course_builder_ids || []).includes(userId);
    
    // Only organizer if assigned as director but NOT as judge or course builder
    return isDirector && !isJudgeAssigned && !isCourseBuilder;
  };
  
  // Can edit competition details (NOT judges - they can only edit events and results)
  const canEditCompetitionDetails = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    
    const userId = user.id;
    const isDirector = (competition.director_ids || []).includes(userId);
    const isCourseBuilder = (competition.course_builder_ids || []).includes(userId);
    
    // Only directors (organizers) and course builders can edit competition details
    // Judges cannot edit competition details
    return isDirector || isCourseBuilder;
  };
  
  // Check if user is only a judge (not admin, not director, not course builder)
  const isOnlyJudge = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return false;
    
    const userId = user.id;
    const isDirector = (competition.director_ids || []).includes(userId);
    const isJudgeAssigned = (competition.judge_ids || []).includes(userId);
    const isCourseBuilder = (competition.course_builder_ids || []).includes(userId);
    
    // Only judge if assigned as judge but NOT as director or course builder
    return isJudgeAssigned && !isDirector && !isCourseBuilder;
  };
  
  // Can manage entries (add riders after deadline) - NOT for organizers only
  // Permission-based access control using LAUSZ roles
  const canManageEntries = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    // Use backend permissions if available
    if (competition.user_permissions) {
      return competition.user_permissions.can_manage_entries;
    }
    // Fallback: versenyszervező or versenyiroda
    return (competition.director_ids || []).includes(user.id) || 
           (competition.versenyiroda_ids || []).includes(user.id);
  };
  
  // Can enter results - bírók and vezetőbíró
  const canEnterResults = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_record_results;
    }
    // Fallback: vezetőbíró, bíró, stílusbíró, díjlovasbíró, lovastusabíró
    return (competition.vezetobiro_ids || []).includes(user.id) ||
           (competition.judge_ids || []).includes(user.id) ||
           (competition.stilusbiro_ids || []).includes(user.id) ||
           (competition.dijlovasbiro_ids || []).includes(user.id) ||
           (competition.lovastusabiro_ids || []).includes(user.id);
  };
  
  // Can modify start time - versenyszervező, versenyiroda
  const canModifyStartTime = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_manage_startlist;
    }
    return (competition.director_ids || []).includes(user.id) ||
           (competition.versenyiroda_ids || []).includes(user.id);
  };
  
  // Can upload course map - pályaépítő only
  const canUploadCourseMap = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_edit_course;
    }
    return (competition.course_builder_ids || []).includes(user.id);
  };
  
  // Can approve results - vezetőbíró, bíró, versenyszervező
  const canApproveResults = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_approve_results;
    }
    return (competition.vezetobiro_ids || []).includes(user.id) ||
           (competition.judge_ids || []).includes(user.id) ||
           (competition.director_ids || []).includes(user.id);
  };
  
  // Can view payments - versenyszervező, versenyiroda
  const canViewPayments = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_view_payments;
    }
    return (competition.director_ids || []).includes(user.id) ||
           (competition.versenyiroda_ids || []).includes(user.id);
  };
  
  // Can manage competition - versenyszervező only
  const canManageCompetition = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_manage_competition;
    }
    return (competition.director_ids || []).includes(user.id);
  };
  
  // Can print documents - vezetőbíró, bíró, versenyszervező, pályaépítő, versenyiroda
  const canPrintDocuments = () => {
    if (!user || !competition) return false;
    if (user.is_admin) return true;
    if (competition.user_permissions) {
      return competition.user_permissions.can_print_documents;
    }
    return (competition.vezetobiro_ids || []).includes(user.id) ||
           (competition.judge_ids || []).includes(user.id) ||
           (competition.director_ids || []).includes(user.id) ||
           (competition.course_builder_ids || []).includes(user.id) ||
           (competition.versenyiroda_ids || []).includes(user.id);
  };
  
  // Upload course map PDF
  const handleCourseMapUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Csak PDF fájl tölthető fel!");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("A fájl mérete nem lehet nagyobb mint 10MB!");
      return;
    }
    
    setCourseMapUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/competitions/${competition.id}/events/${event.id}/course-map?token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Pályarajz sikeresen feltöltve!");
      setShowCourseMapModal(false);
      fetchCompetitions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba a feltöltés során");
    }
    setCourseMapUploading(false);
  };
  
  // Delete course map
  const handleDeleteCourseMap = async () => {
    if (!confirm("Biztosan törölni szeretnéd a pályarajzot?")) return;
    
    try {
      await axios.delete(`${API}/competitions/${competition.id}/events/${event.id}/course-map?token=${token}`);
      toast.success("Pályarajz törölve!");
      fetchCompetitions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba a törlés során");
    }
  };
  
  // Generate optimal startlist - spreads riders with multiple horses
  const generateOptimalStartlist = async () => {
    if (!event || !competition) return;
    
    try {
      const res = await axios.post(`${API}/competition/events/${event.id}/generate-startlist?token=${token}&competition_id=${competition.id}`);
      toast.success(res.data.message || "Startlista automatikusan rendezve!");
      fetchStartlist(competition.id, event.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba a rendezés során");
    }
  };

  const updateResultField = async (entryId, field, value) => {
    setResultsData(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], [field]: value }
    }));
  };

  const saveEntryResult = async (entryId, overrideData = null) => {
    // Use override data if provided (for immediate status saves), otherwise use state
    const data = overrideData || resultsData[entryId];
    if (!data) return;

    // Helper: only return finite numbers, undefined otherwise (axios omits undefined params)
    const safeNum = (val) => {
      const n = parseFloat(val);
      return Number.isFinite(n) ? n : undefined;
    };

    // Build params based on judging type to avoid sending NaN
    const params = {
      token,
      is_disqualified: data.status === "kizarva",
      did_not_finish: data.status === "feladta"
    };

    // Common fields for most judging types
    if (!data.status) {
      params.penalty_points = safeNum(data.penalty_points) ?? 0;
      params.time_seconds = safeNum(data.time_seconds) ?? 0;
    } else {
      params.penalty_points = 0;
      params.time_seconds = 0;
    }

    // Only add judging-type specific fields when they have valid values
    const judgingType = event?.judging_type;
    
    if (judgingType === 'stilus') {
      const styleVal = safeNum(data.style_points);
      if (styleVal !== undefined) params.style_points = styleVal;
    }
    
    if (judgingType === 'dijlovaglas') {
      const dressageVal = safeNum(data.dressage_points);
      if (dressageVal !== undefined) params.dressage_points = dressageVal;
    }
    
    if (judgingType === 'ket_fazis') {
      // Phase 1
      const p1Penalty = safeNum(data.phase1_penalty);
      if (p1Penalty !== undefined) params.phase1_penalty = p1Penalty;
      const p1Time = safeNum(data.phase1_time);
      if (p1Time !== undefined) params.phase1_time = p1Time;
      params.phase1_eliminated = data.phase1_eliminated || false;
      params.phase1_dnf = data.phase1_dnf || false;
      
      // Phase 2
      const p2Penalty = safeNum(data.phase2_penalty);
      if (p2Penalty !== undefined) params.phase2_penalty = p2Penalty;
      const p2Time = safeNum(data.phase2_time);
      if (p2Time !== undefined) params.phase2_time = p2Time;
      params.phase2_eliminated = data.phase2_eliminated || false;
      params.phase2_dnf = data.phase2_dnf || false;
    }

    try {
      await axios.put(`${API}/competition/entries/${entryId}/result`, null, { params });
      console.log("Result saved for entry:", entryId, params);
    } catch (err) {
      console.error("Failed to save result", err);
      toast.error("Hiba az eredmény mentésekor");
    }
  };

  const saveEventStartTime = async () => {
    if (!competition || !event || !eventStartTime) return;
    try {
      await axios.put(
        `${API}/competitions/${competition.id}/events/${event.id}/start-time`,
        null,
        { params: { token, actual_start_time: eventStartTime } }
      );
      // Refresh competition to get updated event times
      await fetchCompetition(competition.id);
      await fetchStartlist(competition.id, event.id);
      toast.success("Kezdési idő mentve!");
    } catch (err) {
      console.error("Failed to save event start time", err);
      toast.error("Hiba a kezdési idő mentésekor");
    }
  };

  const toggleStatus = async (entryId, status) => {
    const currentData = resultsData[entryId] || {};
    const currentStatus = currentData.status;
    const newStatus = currentStatus === status ? "" : status;
    
    // Create the new data object immediately
    const newData = { ...currentData, status: newStatus };
    
    // Update state
    setResultsData(prev => ({
      ...prev,
      [entryId]: newData
    }));

    // Save immediately with the new data (not from stale closure)
    await saveEntryResult(entryId, newData);
  };

  const calculateResults = () => {
    const judgingType = event?.judging_type || 'hiba_ido';
    
    // Sort function based on judging type
    const sortKey = (e) => {
      if (judgingType === 'dijlovaglas') {
        // Díjlovaglás: higher percentage/points is better
        const percentage = parseFloat(e.dressage_percentage) || 0;
        const points = parseFloat(e.dressage_points) || 0;
        return { primary: -percentage, secondary: -points, tertiary: 0 };
      } else if (judgingType === 'stilus' || judgingType === 'pontgyujto' || judgingType === 'fokozatos' || judgingType === 'caprilli') {
        // Higher points better
        const points = parseFloat(e.style_points) || parseFloat(e.collected_points) || parseFloat(e.dressage_points) || 0;
        return { primary: -points, secondary: 0, tertiary: 0 };
      } else if (judgingType === 'vadaszugratas') {
        // C-rule: penalties become seconds, total time decides
        const time = parseFloat(e.time_seconds) || 0;
        const penaltySeconds = (parseFloat(e.penalty_points) || 0) * 4;
        return { primary: time + penaltySeconds, secondary: 0, tertiary: 0 };
      } else if (judgingType === 'idealis_ido') {
        // Closest to ideal time
        const idealTime = event?.ideal_time || event?.base_time || 60;
        const time = parseFloat(e.time_seconds) || 999999;
        return { primary: Math.abs(time - idealTime), secondary: 0, tertiary: 0 };
      } else {
        // Default hiba_ido: penalty points then time
        return {
          primary: parseFloat(e.penalty_points) || 0,
          secondary: parseFloat(e.time_seconds) || 999999,
          tertiary: 0
        };
      }
    };
    
    const compareKeys = (a, b) => {
      if (a.primary !== b.primary) return a.primary - b.primary;
      if (a.secondary !== b.secondary) return a.secondary - b.secondary;
      return a.tertiary - b.tertiary;
    };
    
    const keysEqual = (a, b) => {
      return a.primary === b.primary && a.secondary === b.secondary && a.tertiary === b.tertiary;
    };
    
    // Separate valid entries from DQ/DNF
    const validEntries = [...startlist]
      .map(entry => ({ ...entry, ...resultsData[entry.id] }))
      .filter(e => !e.status && !e.is_disqualified && !e.did_not_finish);
    
    const dnfEntries = [...startlist]
      .map(entry => ({ ...entry, ...resultsData[entry.id] }))
      .filter(e => e.status === 'feladta' || e.did_not_finish);
    
    const dqEntries = [...startlist]
      .map(entry => ({ ...entry, ...resultsData[entry.id] }))
      .filter(e => e.status === 'kizarva' || e.is_disqualified);
    
    // Sort valid entries by judging type
    const sorted = validEntries
      .map(e => ({ ...e, _sortKey: sortKey(e) }))
      .sort((a, b) => compareKeys(a._sortKey, b._sortKey));
    
    // Assign dense ranks (1, 1, 2 for ties)
    let denseRank = 0;
    let prevKey = null;
    const rankedEntries = sorted.map((entry, idx) => {
      const currentKey = entry._sortKey;
      if (prevKey === null || !keysEqual(currentKey, prevKey)) {
        denseRank++;
      }
      prevKey = currentKey;
      return { ...entry, calculated_rank: denseRank };
    });
    
    // Add DNF and DQ at the end
    const dnfWithStatus = dnfEntries.map(e => ({ ...e, calculated_rank: null, rank_status: 'FELADTA' }));
    const dqWithStatus = dqEntries.map(e => ({ ...e, calculated_rank: null, rank_status: 'KIZÁRVA' }));

    setCalculatedResults([...rankedEntries, ...dnfWithStatus, ...dqWithStatus]);
  };

  const submitResults = async () => {
    try {
      // First calculate and save results to DB
      await axios.post(`${API}/competition/calculate-results/${competitionId}/${eventId}`, null, {
        params: { token }
      });

      // Submit for approval instead of immediately publishing
      setSubmittingForApproval(true);
      await axios.post(`${API}/competition/events/${eventId}/submit-for-approval`, {
        competition_id: competitionId
      }, { params: { token } });

      alert("Eredmények elküldve jóváhagyásra! A bírók és versenyszervezők email értesítést kaptak.");
      setSubmittingForApproval(false);
      
      // Refresh competition to get updated approval status
      await fetchCompetition(competitionId);
      await fetchStartlist(competitionId, eventId);
      
      setScoringMode(false);
      setCalculatedResults(null);
    } catch (err) {
      setSubmittingForApproval(false);
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba az eredmények küldésekor");
    }
  };

  const handleApproveResults = async (action) => {
    try {
      const res = await axios.post(`${API}/competition/events/${eventId}/approve-results`, {
        competition_id: competitionId,
        action: action // "approve" or "reject"
      }, { params: { token } });

      if (action === "approve") {
        toast.success("Eredmények jóváhagyva!");
      } else {
        toast.info("Eredmények elutasítva. A bíró módosíthatja és újra beküldheti.");
      }
      
      setApprovalStatus(res.data);
      // Refresh both competition and startlist to update UI
      await fetchCompetition(competitionId);
      await fetchStartlist(competitionId, eventId);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : "Hiba a jóváhagyás során");
    }
  };

  // ==================== COMPETITION CRUD ====================
  const handleCreateCompetition = async () => {
    if (!createForm.name || !createForm.date) return;
    
    // Validate required fields for special judging types
    for (const ef of eventForms) {
      if (ef.judging_type === 'alapido' && !ef.base_time) {
        alert(`Az "${ef.name || 'Névtelen versenyszám'}" versenyszámhoz kötelező megadni az alapidőt!`);
        return;
      }
      if (ef.judging_type === 'dijlovaglas' && !ef.max_points) {
        alert(`Az "${ef.name || 'Névtelen versenyszám'}" versenyszámhoz kötelező megadni a maximum pontszámot!`);
        return;
      }
    }
    
    try {
      const compData = {
        name: createForm.name,
        date: createForm.date,
        start_time: createForm.start_time,
        description: createForm.description,
        entry_deadline: createForm.entry_deadline || createForm.date,
        location: "Gepárd Lovarda",
        director_ids: createForm.director_ids || [],
        judge_ids: createForm.judge_ids || [],
        course_builder_ids: createForm.course_builder_ids || [],
        vezetobiro_ids: createForm.vezetobiro_ids || [],
        stilusbiro_ids: createForm.stilusbiro_ids || [],
        dijlovasbiro_ids: createForm.dijlovasbiro_ids || [],
        lovastusabiro_ids: createForm.lovastusabiro_ids || [],
        melegitobiro_ids: createForm.melegitobiro_ids || [],
        versenyiroda_ids: createForm.versenyiroda_ids || [],
        entry_fee_enabled: createForm.entry_fee_enabled || false,
        entry_fee_tier1_limit: createForm.entry_fee_tier1_limit || 2,
        entry_fee_tier1_price: createForm.entry_fee_tier1_price || 3000,
        entry_fee_tier2_price: createForm.entry_fee_tier2_price || 2500,
        events: eventForms.map(ef => ({
          name: ef.name,
          category: ef.category,
          judging_type: ef.judging_type,
          description: ef.description,
          height: ef.height || "",
          base_time: ef.base_time || null,
          max_points: ef.max_points || null
        }))
      };
      
      await axios.post(`${API}/competitions`, compData, { params: { token } });
      alert("Verseny létrehozva! A kiválasztott hivatalos személyek email értesítést kapnak.");
      setShowCreateModal(false);
      setCreateForm({ name: "", date: "", start_time: "09:00", description: "", entry_deadline: "", director_ids: [], judge_ids: [], course_builder_ids: [], entry_fee_enabled: false, entry_fee_tier1_limit: 2, entry_fee_tier1_price: 3000, entry_fee_tier2_price: 2500 });
      setEventForms([]);
      await fetchCompetitions();
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba");
    }
  };

  const handleUpdateCompetition = async () => {
    if (!editForm) return;
    
    // Validate required fields for special judging types
    for (const ev of editForm.events || []) {
      if (ev.judging_type === 'alapido' && !ev.base_time) {
        alert(`Az "${ev.name || 'Névtelen versenyszám'}" versenyszámhoz kötelező megadni az alapidőt!`);
        return;
      }
      if (ev.judging_type === 'dijlovaglas' && !ev.max_points) {
        alert(`Az "${ev.name || 'Névtelen versenyszám'}" versenyszámhoz kötelező megadni a maximum pontszámot!`);
        return;
      }
    }
    
    try {
      await axios.put(`${API}/competitions/${competitionId}`, editForm, { params: { token } });
      alert("Verseny frissítve!");
      setShowEditModal(false);
      await fetchCompetition(competitionId);
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(typeof msg === 'string' ? msg : "Hiba a frissítés során");
    }
  };

  const handleCancelCompetition = async () => {
    if (!confirm("Biztosan elmarad a verseny? Ez nem visszavonható.")) return;
    try {
      await axios.post(`${API}/competitions/${competitionId}/cancel`, null, { params: { token } });
      alert("Verseny elmaradtnak jelölve!");
      navigate('/verseny');
    } catch (err) {
      alert("Hiba a verseny lemondásakor");
    }
  };

  const handleDeleteCompetition = async () => {
    if (!confirm("Biztosan törölni szeretnéd a versenyt? Ez nem visszavonható.")) return;
    try {
      await axios.delete(`${API}/competitions/${competitionId}`, { params: { token } });
      alert("Verseny törölve!");
      navigate('/verseny');
    } catch (err) {
      alert("Hiba a verseny törlésekor");
    }
  };

  const addEventForm = () => {
    setEventForms([...eventForms, {
      name: "", category: "Díjugratás", judging_type: "hiba_ido", description: "", height: ""
    }]);
  };

  const updateEventForm = (index, field, value) => {
    const updated = [...eventForms];
    updated[index][field] = value;
    setEventForms(updated);
  };

  const removeEventForm = (index) => {
    setEventForms(eventForms.filter((_, i) => i !== index));
  };

  // ==================== HELPERS ====================
  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    return new Date() > new Date(deadline);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" });
  };

  const getTotalEntries = (comp) => {
    if (!comp.events) return 0;
    return comp.events.reduce((sum, ev) => sum + (ev.entry_count || 0), 0);
  };

  const getEventsByCategory = (events) => {
    const grouped = {};
    CATEGORY_ORDER.forEach(cat => grouped[cat] = []);
    events?.forEach(ev => {
      const cat = ev.category || "Díjugratás";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(ev);
    });
    return grouped;
  };

  const canCreateCompetition = () => user?.roles?.includes("versenyszervezo") || user?.is_admin;

  const getCompetitionColor = (comp) => {
    // Elmaradt verseny = piros
    if (comp.status === "cancelled" || comp.status === "elmaradt") {
      return "text-red-500";
    }
    const deadline = comp.entry_deadline;
    if (!deadline) return "text-slate-800";
    return isDeadlinePassed(deadline) ? "text-orange-500" : "text-emerald-600";
  };

  // Filter competitions based on tab
  const getFilteredCompetitions = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    
    return competitions.filter(comp => {
      const compDate = new Date(comp.date);
      compDate.setHours(0, 0, 0, 0);
      
      const isCancelled = comp.status === "cancelled" || comp.status === "elmaradt";
      const isDeleted = comp.status === "deleted" || comp.status === "torolve";
      const isPast = compDate < today;
      
      if (activeTab === "elmult") {
        // Elmúlt versenyek: befejezett (dátum < ma), DE NEM törölt és NEM elmaradt
        return isPast && !isCancelled && !isDeleted;
      } else {
        // Fő lista: 
        // - Jövőbeli vagy mai versenyek (nem elmaradt/törölt)
        // - Elmaradt versenyek: csak aznap éjfélig maradnak láthatók
        if (isDeleted) return false;
        if (isPast && !isCancelled) return false; // Elmúlt normál versenyek nem jelennek meg
        if (isCancelled) {
          // Elmaradt verseny: csak aznap látható
          const compDateEnd = new Date(comp.date);
          compDateEnd.setHours(23, 59, 59, 999);
          return compDateEnd >= today && compDateEnd <= endOfToday;
        }
        return true; // Jövőbeli normál versenyek
      }
    });
  };

  const getEventColor = (ev) => {
    // Purple: BOTH judge AND organizer approved (results_approved = true)
    if (ev.results_approved) return "text-purple-600";
    
    // Orange: Results pending approval (at least one approved, waiting for the other)
    if (ev.results_pending_approval) return "text-orange-500";
    
    // Has results but not yet submitted for approval
    if (ev.has_results) return "text-orange-500";
    
    // Default colors based on deadline
    const deadline = ev.entry_deadline || competition?.entry_deadline;
    if (!deadline) return "text-slate-800";
    return isDeadlinePassed(deadline) ? "text-orange-500" : "text-emerald-600";
  };

  // Get rider's horse count in this event
  const getRiderHorseCount = (riderId, userId) => {
    const id = riderId || userId;
    if (!id) return 1;
    return startlist.filter(e => (e.rider_id || e.user_id) === id).length;
  };

  // Get color for rider based on horse count
  const getRiderColor = (entry) => {
    const count = getRiderHorseCount(entry.rider_id, entry.user_id);
    if (count >= 3) return "text-red-600 font-semibold";
    if (count === 2) return "text-blue-600 font-semibold";
    return "text-slate-800";
  };

  const pad = (n) => n.toString().padStart(2, '0');

  // ==================== USER MENU ====================
  const userMenuEl = (
    <div className="relative">
      {user ? (
        <ProfileMenu user={user} onLogout={handleLogout}>
          {user.roles?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {user.roles.map(role => (
                <span key={role} className="px-2 py-0.5 bg-orange-50 text-[#EA6C0A] text-xs rounded-full">
                  {role === "versenyszervezo" ? "Szervező" : role === "biro" ? "Bíró" : role === "palyaepito" ? "Pályaépítő" : role}
                </span>
              ))}
            </div>
          )}
        </ProfileMenu>
      ) : (
        <button
          data-testid="open-login-btn"
          onClick={() => setShowAuthModal(true)}
          className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
        >
          <LogIn size={20} className="text-slate-600" />
        </button>
      )}
    </div>
  );

  // ==================== COUNTDOWN ====================
  const CountdownDisplay = ({ small = false }) => {
    const textSize = small ? "text-lg" : "text-2xl";
    
    if (countdown.expired) {
      return (
        <div className="text-center">
          <p className="text-red-500 font-medium">A nevezési idő lejárt</p>
        </div>
      );
    }
    
    return (
      <div className="text-center">
        <div className={`font-mono font-bold ${textSize} text-emerald-600 flex items-center justify-center gap-1`}>
          <span>{pad(countdown.days)}</span>
          <span className="text-slate-400 text-xs font-normal">nap</span>
          <span className="mx-1">{pad(countdown.hours)}</span>
          <span className="text-slate-400 text-xs font-normal">óra</span>
          <span className="mx-1">{pad(countdown.minutes)}</span>
          <span className="text-slate-400 text-xs font-normal">perc</span>
          <span className="mx-1">{pad(countdown.seconds)}</span>
          <span className="text-slate-400 text-xs font-normal">mp</span>
        </div>
        <p className="text-slate-500 text-xs mt-1">Nevezési határidő</p>
      </div>
    );
  };

  // ==================== RENDER: LOADING ====================
  if (loading) {
    return (
      <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ==================== RENDER: USER MANAGEMENT MODAL ====================
  const UserManagementModal = () => (
    <AnimatePresence>
      {showUserManagement && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800">Felhasználók kezelése</h3>
              <button onClick={() => { setShowUserManagement(false); setSelectedUserId(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            {selectedUserId ? (
              // User detail view
              <div>
                <button onClick={() => setSelectedUserId(null)} className="flex items-center gap-2 text-slate-600 mb-4 hover:text-blue-600">
                  <ArrowLeft size={18} /> Vissza
                </button>
                {(() => {
                  const selectedUser = allUsers.find(u => u.id === selectedUserId);
                  if (!selectedUser) return null;
                  return (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-4">{selectedUser.name}</h4>
                      <p className="text-slate-500 mb-6">{selectedUser.email}</p>
                      
                      <h5 className="font-medium text-slate-700 mb-3">Jogosultságok</h5>
                      <div className="space-y-2">
                        {ROLES.map(role => {
                          const hasRole = selectedUser.roles?.includes(role.id);
                          return (
                            <label key={role.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                              <input
                                type="checkbox"
                                checked={hasRole}
                                onChange={() => updateUserRole(selectedUserId, role.id, !hasRole)}
                                className="w-5 h-5 rounded border-slate-300"
                              />
                              <span className="text-slate-800">{role.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              // User list view
              <div className="space-y-2">
                {allUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className="w-full p-4 bg-slate-50 rounded-lg hover:bg-slate-100 flex items-center justify-between"
                  >
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{u.name}</p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                    </div>
                    <div className="flex gap-1">
                      {u.roles?.map(r => (
                        <span key={r} className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                          {r === "versenyszervezo" ? "Szervező" : r === "biro" ? "Bíró" : r}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // ==================== RENDER: EVENT PAGE ====================
  if (eventId && event) {
    const deadline = event.entry_deadline || competition?.entry_deadline;
    const deadlinePassed = isDeadlinePassed(deadline);
    const selectedHorse = horses.find(h => h.id === selectedHorseId);
    
    // Results are visible only if fully approved (2 judges)
    // Until then, only show startlist - judges/admins can see pending status but not results
    const hasRawResults = startlist.some(e => e.final_rank);
    const resultsApproved = event?.results_approved;
    const resultsPending = event?.results_pending_approval;
    
    // Results only visible to everyone when fully approved (2 judges)
    // Judges/admins see the pending status but not the actual results until approved
    const eventHasResults = hasRawResults && resultsApproved;
    
    return (
      <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button onClick={() => navigate(compPath(competition) )} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
                <ArrowLeft size={20} />
                <span className="font-medium">Vissza</span>
              </button>
              
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                {!deadlinePassed && user && !scoringMode && !entryManagementMode && (
                  <Button data-testid="open-entry-modal-btn" onClick={openEntryModal} className="hidden md:flex bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus size={18} className="mr-2" />Nevezés
                  </Button>
                )}
              
              {canManageEntries() && !scoringMode && !entryManagementMode && !eventHasResults && (
                <Button onClick={() => setEntryManagementMode(true)} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Edit2 size={18} className="mr-2" />Nevezés kezelés
                </Button>
              )}
              
              {/* Kezdési idő beállítás gomb - lejárt határidő után */}
              {canModifyStartTime() && startlist.length > 0 && !scoringMode && !entryManagementMode && !eventHasResults && countdown.expired && (
                <Button 
                  onClick={() => {
                    setEventStartTime(event.actual_start_time || competition?.start_time || "09:00");
                    setShowStartTimeModal(true);
                  }} 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <Clock size={18} className="mr-2" />Kezdési idő
                </Button>
              )}
              
              {/* Eredmény rögzítés - megjelenik ha nincs jóváhagyott eredmény VAGY ha elutasították - NEM elmaradt versenyszámnál */}
              {canEnterResults() && startlist.length > 0 && !scoringMode && !entryManagementMode && countdown.expired && !event.cancelled && (
                !eventHasResults && (
                  <Button onClick={() => {
                    setScoringMode(true);
                    setEventStartTime(event.actual_start_time || competition?.start_time || "09:00");
                    // Load existing results data from entries
                    const existingResults = {};
                    startlist.forEach(entry => {
                      existingResults[entry.id] = {
                        penalty_points: entry.penalty_points || '',
                        time_seconds: entry.time_seconds || '',
                        style_points: entry.style_points || '',
                        dressage_points: entry.dressage_points || '',
                        phase1_penalty: entry.phase1_penalty ?? '',
                        phase1_time: entry.phase1_time || '',
                        phase1_eliminated: entry.phase1_eliminated || false,
                        phase1_dnf: entry.phase1_dnf || false,
                        phase2_penalty: entry.phase2_penalty ?? '',
                        phase2_time: entry.phase2_time || '',
                        phase2_eliminated: entry.phase2_eliminated || false,
                        phase2_dnf: entry.phase2_dnf || false,
                        status: entry.is_disqualified ? 'kizarva' : entry.did_not_finish ? 'feladta' : ''
                      };
                    });
                    setResultsData(existingResults);
                  }} className={`${event?.rejected_by?.length > 0 ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-white`}>
                    {event?.rejected_by?.length > 0 ? 'Eredmény módosítás' : 'Eredmény rögzítés'}
                  </Button>
                )
              )}

              {(scoringMode || entryManagementMode) && (
                <Button onClick={() => { setScoringMode(false); setEntryManagementMode(false); setCalculatedResults(null); }} variant="outline">
                  Kilépés
                </Button>
              )}
              
              {/* Pályarajz feltöltés gomb - csak pályaépítők és adminok */}
              {canUploadCourseMap() && !scoringMode && !entryManagementMode && (
                <Button 
                  onClick={() => setShowCourseMapModal(true)} 
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  <FileText size={18} className="mr-2" />
                  {event?.course_map_url ? 'Pályarajz csere' : 'Pályarajz'}
                </Button>
              )}
              
              {userMenuEl}
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          {/* Event Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex-1">
                {/* Calculate event number */}
                {(() => {
                  let eventNumber = 0;
                  const allEvents = competition?.events || [];
                  // Sort events by category order
                  const sortedEvents = [...allEvents].sort((a, b) => {
                    const catA = CATEGORY_ORDER.indexOf(a.category);
                    const catB = CATEGORY_ORDER.indexOf(b.category);
                    return catA - catB;
                  });
                  for (let i = 0; i < sortedEvents.length; i++) {
                    eventNumber++;
                    if (sortedEvents[i].id === eventId) break;
                  }
                  return (
                    <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${event.cancelled ? 'text-red-600' : eventHasResults ? 'text-purple-600' : deadlinePassed ? 'text-orange-500' : 'text-emerald-600'}`}>
                      <span className="text-slate-400 font-normal">{eventNumber})</span> {event.cancelled ? `E L M A R A D - ${event.name}` : event.name}
                    </h1>
                  );
                })()}
                {event.cancelled && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-700 font-medium">Ez a versenyszám elmaradt, eredmény nem rögzíthető.</p>
                  </div>
                )}
                <div className="space-y-1 mb-4">
                  <p className="text-slate-800">
                    <span className="font-medium">Elbírálás:</span> {JUDGING_TYPES[event.judging_type] || event.judging_type}
                  </p>
                  {event.height && (
                    <p className="text-slate-800">
                      <span className="font-medium">Magasság:</span> {event.height}
                    </p>
                  )}
                  {/* Course Map Display - Only for course builders and admins */}
                  {event.course_map_url && canUploadCourseMap() && (
                    <div className="flex items-center gap-2 mt-2">
                      <FileText size={16} className="text-blue-600" />
                      <a 
                        href={`${BACKEND_URL}${event.course_map_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Pályarajz megtekintése
                      </a>
                      <button 
                        onClick={handleDeleteCourseMap}
                        className="text-red-500 hover:text-red-700 ml-2"
                        title="Pályarajz törlése"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {event.description && <p className="text-slate-600">{event.description}</p>}
              </div>
              <div className="bg-slate-50 rounded-xl p-4 md:min-w-[200px]">
                <CountdownDisplay />
              </div>
            </div>

            {!deadlinePassed && user && !scoringMode && !entryManagementMode && (
              <Button data-testid="open-entry-modal-btn-mobile" onClick={openEntryModal} className="md:hidden w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white">
                <Plus size={18} className="mr-2" />Nevezés
              </Button>
            )}
          </div>

          {/* Entry Management Mode */}
          {entryManagementMode && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-slate-800">Nevezések kezelése</h2>
                <div className="flex gap-2">
                  <Button onClick={generateOptimalStartlist} size="sm" className="bg-indigo-500 hover:bg-indigo-600 text-white">
                    <Settings size={16} className="mr-1" />Auto rendezés
                  </Button>
                  <Button data-testid="new-entry-btn" onClick={openEntryModal} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus size={16} className="mr-1" />Új nevezés
                  </Button>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 mb-4">
                💡 Az "Auto rendezés" gomb automatikusan elosztja a több lóval nevező lovasokat (2 ló: eleje-vége, 3 ló: eleje-közepe-vége)
              </p>
              
              <div className="space-y-2">
                {startlist.map((entry, idx) => (
                  <div key={entry.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveEntry(entry.id, 'up')} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                        <GripVertical size={16} />
                      </button>
                    </div>
                    <span className="w-8 text-center font-bold text-blue-600">{idx + 1}.</span>
                    <span className={`flex-1 ${getRiderColor(entry)}`}>{entry.rider_name} ({entry.rider_id?.slice(-6) || entry.user_id?.slice(-6)})</span>
                    <select
                      value={entry.horse_id || ""}
                      onChange={(e) => updateEntryHorse(entry.id, e.target.value)}
                      className="border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 bg-white"
                    >
                      {allHorses.map(h => (
                        <option key={h.id} value={h.id}>{h.name} ({h.hun_id || h.id.slice(-6)})</option>
                      ))}
                    </select>
                    <button onClick={() => deleteEntry(entry.id)} className="text-red-500 hover:text-red-600 p-1">
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scoring Mode */}
          {scoringMode && !calculatedResults && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Eredmények rögzítése</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">#</th>
                      <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Indulás</th>
                      <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Lovas</th>
                      <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Ló</th>
                      
                      {/* Dynamic columns based on judging type */}
                      {event.judging_type === 'dijlovaglas' ? (
                        <>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Pont (max: {event.max_points || 180})</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">%</th>
                        </>
                      ) : event.judging_type === 'stilus' ? (
                        <>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Idő (mp)</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Stíluspont</th>
                        </>
                      ) : event.judging_type === 'ket_fazis' ? (
                        <>
                          <th className="text-center py-3 px-2 text-slate-500 font-medium text-sm" colSpan="3">I. Fázis</th>
                          <th className="text-center py-3 px-2 text-slate-500 font-medium text-sm" colSpan="3">II. Fázis</th>
                        </>
                      ) : event.judging_type === 'alapido' ? (
                        <>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Idő (mp)</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Hibapont</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Időbünt.</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Össz.</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Idő (mp)</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-sm">Hibapont</th>
                        </>
                      )}
                      <th className="text-center py-3 px-2 text-slate-500 font-medium text-sm">Státusz</th>
                    </tr>
                    {/* Sub-header for two-phase */}
                    {event.judging_type === 'ket_fazis' && (
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th colSpan="4"></th>
                        <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Hiba</th>
                        <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Idő</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-medium text-xs">Státusz</th>
                        <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Hiba</th>
                        <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Idő</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-medium text-xs">Státusz</th>
                        <th></th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {startlist.map((entry, idx) => {
                      const data = resultsData[entry.id] || {};
                      const isExcluded = data.status === "kizarva" || data.status === "feladta";
                      const p1Excluded = data.phase1_eliminated || data.phase1_dnf;
                      const p2Excluded = data.phase2_eliminated || data.phase2_dnf;
                      const canDoPhase2 = event.judging_type === 'ket_fazis' && !p1Excluded && (data.phase1_penalty === 0 || data.phase1_penalty === '0');
                      
                      // Calculate default start time based on event start time + 2 min per entry
                      const getCalculatedTime = () => {
                        if (!eventStartTime) return entry.start_time || "";
                        try {
                          const [h, m] = eventStartTime.split(':').map(Number);
                          const totalMinutes = h * 60 + m + (idx * 2);
                          const hours = Math.floor(totalMinutes / 60);
                          const mins = totalMinutes % 60;
                          return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                        } catch {
                          return entry.start_time || "";
                        }
                      };
                      
                      // Calculate time penalty for alapido
                      const calculateTimePenalty = () => {
                        if (event.judging_type !== 'alapido' || !event.base_time) return 0;
                        const time = parseFloat(data.time_seconds) || 0;
                        if (time <= event.base_time) return 0;
                        const timeOver = time - event.base_time;
                        return Math.ceil(timeOver / 2);
                      };
                      
                      const timePenalty = calculateTimePenalty();
                      const totalPenalty = (parseFloat(data.penalty_points) || 0) + timePenalty;
                      
                      // Calculate start time for display (read-only, based on event start time)
                      const getDisplayTime = () => {
                        if (!eventStartTime) return entry.start_time || "-";
                        try {
                          const [h, m] = eventStartTime.split(':').map(Number);
                          const totalMinutes = h * 60 + m + (idx * 2);
                          const hours = Math.floor(totalMinutes / 60);
                          const mins = totalMinutes % 60;
                          return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                        } catch {
                          return entry.start_time || "-";
                        }
                      };
                      
                      return (
                        <tr key={entry.id} className={`border-b border-slate-100 ${isExcluded ? 'bg-slate-50 opacity-60' : ''}`}>
                          <td className="py-3 px-2 font-semibold text-blue-600">{idx + 1}.</td>
                          <td className="py-3 px-2 text-slate-600">{getDisplayTime()}</td>
                          <td className={`py-3 px-2 font-medium ${getRiderColor(entry)}`}>{entry.rider_name} ({entry.rider_id?.slice(-6) || entry.user_id?.slice(-6)})</td>
                          <td className="py-3 px-2 text-slate-600">{entry.horse_name} ({entry.horse_id?.slice(-6)})</td>
                          
                          {/* Dynamic input fields based on judging type */}
                          {event.judging_type === 'dijlovaglas' ? (
                            <>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.1"
                                  data-testid={`scoring-dressage-points-${entry.id}`}
                                  value={data.dressage_points || ""}
                                  onChange={(e) => updateResultField(entry.id, 'dressage_points', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-3 px-2 text-indigo-600 font-medium">
                                {data.dressage_points ? ((parseFloat(data.dressage_points) / (event.max_points || 180)) * 100).toFixed(2) + '%' : '-'}
                              </td>
                            </>
                          ) : event.judging_type === 'stilus' ? (
                            <>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.time_seconds || ""}
                                  onChange={(e) => updateResultField(entry.id, 'time_seconds', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0.00"
                                />
                              </td>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={data.style_points || ""}
                                  onChange={(e) => updateResultField(entry.id, 'style_points', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0"
                                />
                              </td>
                            </>
                          ) : event.judging_type === 'ket_fazis' ? (
                            <>
                              {/* Phase 1 */}
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  value={data.phase1_penalty ?? ""}
                                  onChange={(e) => updateResultField(entry.id, 'phase1_penalty', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded || p1Excluded}
                                  className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.phase1_time || ""}
                                  onChange={(e) => updateResultField(entry.id, 'phase1_time', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded || p1Excluded}
                                  className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0.00"
                                />
                              </td>
                              <td className="py-3 px-2 text-center">
                                <div className="flex justify-center gap-1">
                                  <button
                                    onClick={() => { updateResultField(entry.id, 'phase1_dnf', !data.phase1_dnf); saveEntryResult(entry.id); }}
                                    className={`px-1 py-0.5 text-xs rounded font-medium ${data.phase1_dnf ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-orange-100'}`}
                                  >F</button>
                                  <button
                                    onClick={() => { updateResultField(entry.id, 'phase1_eliminated', !data.phase1_eliminated); saveEntryResult(entry.id); }}
                                    className={`px-1 py-0.5 text-xs rounded font-medium ${data.phase1_eliminated ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-red-100'}`}
                                  >K</button>
                                </div>
                              </td>
                              {/* Phase 2 */}
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  value={data.phase2_penalty ?? ""}
                                  onChange={(e) => updateResultField(entry.id, 'phase2_penalty', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded || !canDoPhase2 || p2Excluded}
                                  className={`w-16 border rounded-lg px-2 py-1 text-sm text-slate-800 ${canDoPhase2 ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-100 text-slate-500'}`}
                                  placeholder={canDoPhase2 ? "0" : "-"}
                                />
                              </td>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.phase2_time || ""}
                                  onChange={(e) => updateResultField(entry.id, 'phase2_time', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded || !canDoPhase2 || p2Excluded}
                                  className={`w-20 border rounded-lg px-2 py-1 text-sm text-slate-800 ${canDoPhase2 ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-100 text-slate-500'}`}
                                  placeholder={canDoPhase2 ? "0.00" : "-"}
                                />
                              </td>
                              <td className="py-3 px-2 text-center">
                                {canDoPhase2 && (
                                  <div className="flex justify-center gap-1">
                                    <button
                                      onClick={() => { updateResultField(entry.id, 'phase2_dnf', !data.phase2_dnf); saveEntryResult(entry.id); }}
                                      className={`px-1 py-0.5 text-xs rounded font-medium ${data.phase2_dnf ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-orange-100'}`}
                                    >F</button>
                                    <button
                                      onClick={() => { updateResultField(entry.id, 'phase2_eliminated', !data.phase2_eliminated); saveEntryResult(entry.id); }}
                                      className={`px-1 py-0.5 text-xs rounded font-medium ${data.phase2_eliminated ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-red-100'}`}
                                    >K</button>
                                  </div>
                                )}
                              </td>
                            </>
                          ) : event.judging_type === 'alapido' ? (
                            <>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.time_seconds || ""}
                                  onChange={(e) => updateResultField(entry.id, 'time_seconds', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder={`${event.base_time || 60}`}
                                />
                              </td>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  value={data.penalty_points || ""}
                                  onChange={(e) => updateResultField(entry.id, 'penalty_points', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-3 px-2 text-orange-600 font-medium text-center">
                                {timePenalty > 0 ? `+${timePenalty}` : '-'}
                              </td>
                              <td className="py-3 px-2 text-indigo-600 font-bold text-center">
                                {totalPenalty}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  data-testid={`scoring-time-${entry.id}`}
                                  value={data.time_seconds || ""}
                                  onChange={(e) => updateResultField(entry.id, 'time_seconds', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0.00"
                                />
                              </td>
                              <td className="py-3 px-2">
                                <input
                                  type="number"
                                  data-testid={`scoring-penalty-${entry.id}`}
                                  value={data.penalty_points || ""}
                                  onChange={(e) => updateResultField(entry.id, 'penalty_points', e.target.value)}
                                  onBlur={() => saveEntryResult(entry.id)}
                                  disabled={isExcluded}
                                  className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
                                  placeholder="0"
                                />
                              </td>
                            </>
                          )}
                          
                          <td className="py-3 px-2">
                            <div className="flex justify-center gap-1">
                              <button
                                data-testid={`scoring-dnf-${entry.id}`}
                                onClick={() => toggleStatus(entry.id, 'feladta')}
                                className={`px-2 py-1 text-xs rounded font-medium ${data.status === 'feladta' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-orange-100'}`}
                              >FEL</button>
                              <button
                                data-testid={`scoring-dq-${entry.id}`}
                                onClick={() => toggleStatus(entry.id, 'kizarva')}
                                className={`px-2 py-1 text-xs rounded font-medium ${data.status === 'kizarva' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-red-100'}`}
                              >KISZ</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Alapidő info banner */}
              {event.judging_type === 'alapido' && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800">
                    <strong>Alapidő:</strong> {event.base_time || 60} mp | 
                    <span className="ml-2">Időbüntetés: +1 hibapont minden megkezdett 2 mp túllépés után</span>
                  </p>
                </div>
              )}

              <div className="mt-6 text-center">
                <Button data-testid="calculate-results-btn" onClick={calculateResults} className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                  Eredmény számítása
                </Button>
              </div>
            </div>
          )}

          {/* Calculated Results */}
          {calculatedResults && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Eredmények</h2>
              <div className="space-y-2">
                {calculatedResults.map((entry, idx) => {
                  const isExcluded = entry.status || entry.rank_status;
                  const isDQ = entry.status === 'kizarva' || entry.rank_status === 'KIZÁRVA';
                  const isDNF = entry.status === 'feladta' || entry.rank_status === 'FELADTA';
                  
                  // Get result display based on judging type
                  const getResultDisplay = () => {
                    if (isExcluded) return null;
                    
                    if (event?.judging_type === 'dijlovaglas') {
                      const percentage = entry.dressage_percentage || (entry.dressage_points && event?.max_points ? 
                        ((entry.dressage_points / event.max_points) * 100).toFixed(2) : '-');
                      return (
                        <>
                          <span className="text-blue-600 font-mono">{entry.dressage_points || '-'} pont</span>
                          <span className="text-slate-800 font-mono">{percentage}%</span>
                        </>
                      );
                    } else if (event?.judging_type === 'stilus') {
                      return (
                        <>
                          <span className="text-blue-600 font-mono">{entry.style_points || '-'} pont</span>
                          <span className="text-slate-500 font-mono">{parseFloat(entry.time_seconds || 0).toFixed(2)} mp</span>
                        </>
                      );
                    } else if (event?.judging_type === 'ket_fazis') {
                      const p1 = entry.phase1_eliminated ? 'KISZ' : entry.phase1_dnf ? 'FEL' : 
                        `${entry.phase1_penalty ?? '-'}/${entry.phase1_time?.toFixed(2) ?? '-'}`;
                      const p2 = entry.phase2_eliminated ? 'KISZ' : entry.phase2_dnf ? 'FEL' :
                        (entry.phase2_time ? `${entry.phase2_penalty ?? '-'}/${entry.phase2_time?.toFixed(2)}` : '-');
                      return (
                        <>
                          <span className="text-blue-600 font-mono">I: {p1}</span>
                          <span className="text-emerald-600 font-mono">II: {p2}</span>
                        </>
                      );
                    } else if (event?.judging_type === 'alapido') {
                      return (
                        <>
                          <span className="text-blue-600 font-mono">{entry.penalty_points || 0} hp</span>
                          <span className="text-slate-800 font-mono">{parseFloat(entry.time_seconds || 0).toFixed(2)} mp</span>
                          <span className="text-orange-600 font-mono">±{entry.time_deviation?.toFixed(2) || '-'}</span>
                        </>
                      );
                    } else {
                      // Default: hiba_ido
                      return (
                        <>
                          <span className="text-blue-600 font-mono">{entry.penalty_points || 0} hp</span>
                          <span className="text-slate-800 font-mono">{parseFloat(entry.time_seconds || 0).toFixed(2)} mp</span>
                        </>
                      );
                    }
                  };
                  
                  // Determine rank to display
                  const displayRank = entry.calculated_rank || entry.final_rank;
                  const isTopThree = displayRank && displayRank <= 3 && !isExcluded;
                  
                  return (
                    <div key={entry.id} className={`flex items-center gap-4 p-3 rounded-lg ${
                      isDQ ? 'bg-red-50 text-red-700' : 
                      isDNF ? 'bg-orange-50 text-orange-700' : 
                      displayRank === 1 ? 'bg-amber-50' : 
                      displayRank === 2 ? 'bg-slate-100' : 
                      displayRank === 3 ? 'bg-orange-50' : 
                      'bg-white border'
                    }`}>
                      <span className={`font-bold text-lg w-12 ${isTopThree ? 'text-amber-600' : ''}`}>
                        {isDQ ? <span className="text-red-600">KIZÁRVA</span> : 
                         isDNF ? <span className="text-orange-600">FELADTA</span> : 
                         displayRank ? `${displayRank}.` : '-'}
                      </span>
                      <span className={`font-medium flex-1 ${getRiderColor(entry)}`}>{entry.rider_name}</span>
                      <span className="text-slate-600">{entry.horse_name}</span>
                      {getResultDisplay()}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex gap-4 justify-center">
                <Button onClick={() => setCalculatedResults(null)} variant="outline">Módosítás</Button>
                <Button onClick={submitResults} disabled={submittingForApproval} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8">
                  <CheckCircle size={18} className="mr-2" />
                  {submittingForApproval ? "Küldés..." : "Jóváhagyásra küldés"}
                </Button>
              </div>

              {/* Approval Info Panel */}
              {canEnterResults() && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Shield size={18} className="text-amber-600" />
                    Jóváhagyás
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Legalább 1 bírónak, aki hozzá van adva a versenyhez, el kell fogadnia az eredményeket.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded ${event?.results_approved ? 'bg-green-500' : event?.results_pending_approval ? 'bg-amber-400' : 'bg-slate-300'}`}></div>
                      <span className="text-sm text-slate-700">Versenyszervező</span>
                      {event?.approved_by?.some(id => competition?.director_ids?.includes(id)) && (
                        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">Elfogadva</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded ${event?.results_approved ? 'bg-green-500' : event?.results_pending_approval ? 'bg-amber-400' : 'bg-slate-300'}`}></div>
                      <span className="text-sm text-slate-700">Bíró</span>
                      {event?.approved_by?.some(id => competition?.judge_ids?.includes(id)) && (
                        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">Elfogadva</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Startlist / Results */}
          {!scoringMode && !entryManagementMode && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Users size={20} className="text-blue-600" />
                  {eventHasResults ? 'Eredmények' : event?.results_pending_approval ? 'Eredmények (jóváhagyásra vár)' : 'Startlista'}
                </h2>
                {startlist.length > 0 && (
                  <div>
                    {eventHasResults ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}/competition/startlist-pdf/${competitionId}/${eventId}?include_results=true`, '_blank')}
                        className="flex items-center gap-2 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                      >
                        <Trophy size={16} />
                        Eredmény PDF
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}/competition/startlist-pdf/${competitionId}/${eventId}`, '_blank')}
                        className="flex items-center gap-2"
                      >
                        <FileDown size={16} />
                        Startlista PDF
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Approval Pending Banner - Only for judges/admins */}
              {event?.results_pending_approval && !event?.results_approved && canEnterResults() && (
                <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock size={20} className="text-amber-600" />
                    <span className="font-semibold text-slate-800">Eredmények jóváhagyásra várnak</span>
                  </div>
                  
                  {event?.rejected_by?.length > 0 && (
                    <div className="mb-3 p-2 bg-red-100 rounded text-red-700 text-sm">
                      ⚠️ {event.rejected_by.length} elutasítás érkezett. Az eredmények módosíthatók és újra beküldhetők.
                    </div>
                  )}
                  
                  {/* Check if current user already approved */}
                  {event?.approved_by?.includes(user?.id) ? (
                    <div className="p-3 bg-green-100 rounded-lg text-green-700 flex items-center gap-2">
                      <CheckCircle size={18} />
                      <span className="font-medium">Már elfogadtad az eredményeket</span>
                    </div>
                  ) : event?.rejected_by?.includes(user?.id) ? (
                    <div className="p-3 bg-red-100 rounded-lg text-red-700 flex items-center gap-2">
                      <X size={18} />
                      <span className="font-medium">Már elutasítottad az eredményeket</span>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <Button onClick={() => handleApproveResults('approve')} className="bg-green-500 hover:bg-green-600 text-white">
                        <CheckCircle size={18} className="mr-2" />Elfogadás
                      </Button>
                      <Button onClick={() => handleApproveResults('reject')} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                        <X size={18} className="mr-2" />Elutasítás
                      </Button>
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <p className="text-sm text-slate-600 mb-2">Jóváhagyási státusz (2 jóváhagyás szükséges):</p>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">✓ Elfogadva: {event?.approved_by?.length || 0}/2</span>
                      <span className="text-red-600">✗ Elutasítva: {event?.rejected_by?.length || 0}</span>
                    </div>
                    {event?.results_approved && (
                      <p className="text-green-600 font-medium mt-2">✅ Az eredmények jóváhagyva és publikálva!</p>
                    )}
                  </div>
                </div>
              )}

              {/* Show message if pending and user is not judge/admin */}
              {event?.results_pending_approval && !event?.results_approved && !canEnterResults() && (
                <div className="mb-6 p-4 bg-slate-100 rounded-lg text-center">
                  <Clock size={24} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Az eredmények jóváhagyásra várnak. Még {2 - (event?.approved_by?.length || 0)} bíró jóváhagyása szükséges.</p>
                </div>
              )}
              
              {startlist.length === 0 ? (
                <p className="text-slate-500 text-center py-8">A startlista még nem került közzétételre.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium text-sm">{eventHasResults ? 'Hely.' : 'Rajt.'}</th>
                        {!eventHasResults && <th className="text-left py-3 px-4 text-slate-500 font-medium text-sm">Időpont</th>}
                        <th className="text-left py-3 px-4 text-slate-500 font-medium text-sm">Lovas</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium text-sm">Ló</th>
                        {eventHasResults && (
                          <>
                            {/* Dynamic columns based on judging type */}
                            {event.judging_type === 'dijlovaglas' ? (
                              <>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Pont</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">%</th>
                              </>
                            ) : event.judging_type === 'stilus' ? (
                              <>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Stíluspont</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Idő</th>
                              </>
                            ) : event.judging_type === 'ket_fazis' ? (
                              <>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">I. Fázis</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">II. Fázis</th>
                              </>
                            ) : event.judging_type === 'alapido' ? (
                              <>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Hibapont</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Idő</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Eltérés</th>
                              </>
                            ) : (
                              <>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Hibapont</th>
                                <th className="text-center py-3 px-4 text-slate-500 font-medium text-sm">Idő</th>
                              </>
                            )}
                          </>
                        )}
                        {!eventHasResults && !countdown.expired && user && <th className="w-10"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(eventHasResults ? [...startlist].sort((a, b) => {
                        // Kizárt és Feladta mindig a lista aljára kerül
                        const aDisqualified = a.is_disqualified || a.rank_status === 'KIZÁRVA';
                        const bDisqualified = b.is_disqualified || b.rank_status === 'KIZÁRVA';
                        const aDNF = a.did_not_finish || a.rank_status === 'FELADTA';
                        const bDNF = b.did_not_finish || b.rank_status === 'FELADTA';
                        
                        // Disqualified goes to the very bottom
                        if (aDisqualified && !bDisqualified) return 1;
                        if (!aDisqualified && bDisqualified) return -1;
                        
                        // DNF goes after normal but before disqualified
                        if (aDNF && !bDNF && !bDisqualified) return 1;
                        if (!aDNF && !aDisqualified && bDNF) return -1;
                        
                        // Both normal - sort by final_rank
                        return (a.final_rank || 999) - (b.final_rank || 999);
                      }) : startlist).map((entry, idx) => {
                        // Calculate start time from event.actual_start_time
                        const getCalculatedStartTime = () => {
                          const eventStart = event.actual_start_time || competition?.start_time;
                          if (!eventStart) return entry.start_time || "-";
                          try {
                            const [h, m] = eventStart.split(':').map(Number);
                            const totalMinutes = h * 60 + m + (idx * 2);
                            const hours = Math.floor(totalMinutes / 60);
                            const mins = totalMinutes % 60;
                            return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                          } catch {
                            return entry.start_time || "-";
                          }
                        };
                        
                        return (
                          <tr key={entry.id || idx} className={`border-b border-slate-100 hover:bg-slate-50 ${(entry.is_disqualified || entry.rank_status === 'KIZÁRVA') ? 'bg-red-50' : (entry.did_not_finish || entry.rank_status === 'FELADTA') ? 'bg-orange-50' : ''}`}>
                            <td className="py-3 px-4 font-semibold text-blue-600">
                              {eventHasResults ? (
                                entry.is_disqualified || entry.rank_status === 'KIZÁRVA' ? 
                                  <span className="text-red-600">KIZÁRVA</span> : 
                                entry.did_not_finish || entry.rank_status === 'FELADTA' ? 
                                  <span className="text-orange-600">FELADTA</span> : 
                                entry.final_rank ? `${entry.final_rank}.` : '-'
                              ) : `${entry.start_number || idx + 1}.`}
                            </td>
                            {!eventHasResults && <td className="py-3 px-4 text-slate-600">{getCalculatedStartTime()}</td>}
                            <td className={`py-3 px-4 font-medium ${getRiderColor(entry)}`}>{entry.rider_name} ({entry.rider_id?.slice(-6) || entry.user_id?.slice(-6)})</td>
                            <td className="py-3 px-4 text-slate-600">{entry.horse_name} ({entry.horse_id?.slice(-6)})</td>
                            {eventHasResults && (
                            <>
                              {/* Dynamic result columns based on judging type */}
                              {event.judging_type === 'dijlovaglas' ? (
                                <>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified ? <span className="text-red-600 font-bold">KIZÁRVA</span> : entry.did_not_finish ? <span className="text-red-600 font-bold">FELADTA</span> : (entry.dressage_points ?? '-')}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified || entry.did_not_finish ? '-' : (entry.dressage_percentage ? `${entry.dressage_percentage.toFixed(2)}%` : '-')}
                                  </td>
                                </>
                              ) : event.judging_type === 'stilus' ? (
                                <>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified ? <span className="text-red-600 font-bold">KIZÁRVA</span> : entry.did_not_finish ? <span className="text-red-600 font-bold">FELADTA</span> : (entry.style_points ?? '-')}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified || entry.did_not_finish ? '-' : (entry.time_seconds?.toFixed(2) ?? '-')}
                                  </td>
                                </>
                              ) : event.judging_type === 'ket_fazis' ? (
                                <>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.phase1_eliminated || entry.phase1_dnf ? (entry.phase1_eliminated ? 'KISZ' : 'FEL') : 
                                      `${entry.phase1_penalty ?? '-'} / ${entry.phase1_time?.toFixed(2) ?? '-'}`}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.phase2_eliminated || entry.phase2_dnf ? (entry.phase2_eliminated ? 'KISZ' : 'FEL') :
                                      (entry.phase2_penalty !== null && entry.phase2_penalty !== undefined) ? 
                                      `${entry.phase2_penalty} / ${entry.phase2_time?.toFixed(2) ?? '-'}` : '-'}
                                  </td>
                                </>
                              ) : event.judging_type === 'alapido' ? (
                                <>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified ? <span className="text-red-600 font-bold">KIZÁRVA</span> : entry.did_not_finish ? <span className="text-red-600 font-bold">FELADTA</span> : (entry.penalty_points ?? '-')}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified || entry.did_not_finish ? '-' : (entry.time_seconds?.toFixed(2) ?? '-')}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified || entry.did_not_finish ? '-' : (entry.time_deviation?.toFixed(2) ?? '-')}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified ? <span className="text-red-600 font-bold">KIZÁRVA</span> : entry.did_not_finish ? <span className="text-red-600 font-bold">FELADTA</span> : (entry.penalty_points ?? '-')}
                                  </td>
                                  <td className="py-3 px-4 text-center text-slate-700">
                                    {entry.is_disqualified || entry.did_not_finish ? '-' : (entry.time_seconds?.toFixed(2) ?? '-')}
                                  </td>
                                </>
                              )}
                            </>
                          )}
                          {!eventHasResults && !countdown.expired && user && (entry.user_id === user.id || user.is_admin) && (
                            <td className="py-3 px-4">
                              <button onClick={() => deleteEntry(entry.id)} className="text-red-500 hover:text-red-700">
                                <X size={18} />
                              </button>
                            </td>
                          )}
                          {!eventHasResults && !countdown.expired && user && entry.user_id !== user.id && !user.is_admin && (
                            <td className="py-3 px-4"></td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Color legend */}
            </div>
          )}
        </main>

        {/* Entry Modal */}
        <AnimatePresence>
          {showEntryModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">Nevezés</h3>
                  <button onClick={() => setShowEntryModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                </div>

                <div className="space-y-4">
                  {canManageEntries() && (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Lovas</label>
                      <select value={selectedRiderId} onChange={(e) => { setSelectedRiderId(e.target.value); fetchEntryFeeCalc(e.target.value || null); }} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white">
                        <option value="">Saját ({user?.name})</option>
                        {[...allUsers].sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu')).map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} {u.identifier ? `[${u.identifier}]` : `(${u.id.slice(-6)})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Ha nem admin/bíró, de van hozzárendelt fiók, akkor azokat mutassuk */}
                  {!canManageEntries() && linkedAccounts.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Lovas</label>
                      <select value={selectedRiderId} onChange={(e) => { setSelectedRiderId(e.target.value); fetchEntryFeeCalc(e.target.value || null); }} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white">
                        <option value="">Saját ({user?.name})</option>
                        {[...linkedAccounts].sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu')).map(la => (
                          <option key={la.id} value={la.id}>
                            {la.name} {la.identifier ? `[${la.identifier}]` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">Hozzárendelt fiókok a Tag profilodnál kezelhetők</p>
                    </div>
                  )}
                  
                  {!canManageEntries() && linkedAccounts.length === 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Lovas</label>
                      <div className="bg-slate-100 rounded-lg p-3 text-slate-800">{user?.name}</div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Ló</label>
                    <select data-testid="entry-horse-select" value={selectedHorseId} onChange={(e) => setSelectedHorseId(e.target.value)} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white">
                      <option value="">Válassz lovat...</option>
                      {[...(canManageEntries() ? allHorses : linkedAccounts.length > 0 ? allHorses : horses)].sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu')).map(horse => (
                        <option key={horse.id} value={horse.id}>{horse.name} ({horse.hun_id || horse.id.slice(-6)})</option>
                      ))}
                    </select>
                  </div>

                  {/* LAUSZ: 18 év alatti lovas figyelmeztetés */}
                  {isRiderUnder18(selectedRiderId) && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <p className="text-sm font-medium text-amber-800">18 év alatti lovas</p>
                          <p className="text-xs text-amber-700 mt-1">
                            LAUSZ szabályzat szerint 18 év alatti lovas esetén felelős személy (edző, szülő) jelenléte kötelező a versenyen.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedHorseId && (
                    <div className="bg-blue-50 rounded-xl p-4 mt-4">
                      <p className="text-sm text-slate-500 mb-2">Összefoglaló</p>
                      <p className="font-medium text-slate-800">{competition?.name}</p>
                      <p className="text-blue-600">{event?.name}</p>
                      <p className="text-slate-600 mt-2">{selectedRiderId ? (allUsers.find(u => u.id === selectedRiderId)?.name || linkedAccounts.find(la => la.id === selectedRiderId)?.name) : user?.name} – {selectedHorse?.name || allHorses.find(h => h.id === selectedHorseId)?.name}</p>
                    </div>
                  )}

                  {/* Nevezési díj megjelenítése */}
                  {entryFeeCalc?.enabled && (
                    <div data-testid="entry-modal-fee-calc" className="bg-emerald-50 rounded-xl p-4 mt-4 border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800 mb-2">Nevezési díj</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            {selectedRiderId ? `${allUsers.find(u => u.id === selectedRiderId)?.name || 'Lovas'} eddigi startjai:` : 'Eddigi startjaid ezen a versenyen:'}
                          </span>
                          <span data-testid="fee-current-entries" className="font-medium text-slate-800">{entryFeeCalc.current_entries} db</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Eddigi összeg:</span>
                          <span data-testid="fee-total-current" className="font-medium text-slate-800">{entryFeeCalc.total_current?.toLocaleString('hu-HU')} Ft</span>
                        </div>
                        <div className="border-t border-emerald-200 pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="text-emerald-700 font-medium">Ez a nevezés:{entryFeeCalc.new_event_is_free ? " (díjmentes)" : entryFeeCalc.new_event_custom_fee ? " (egyedi díj)" : ""}</span>
                            <span data-testid="fee-new-entry-price" className="font-bold text-emerald-700">{entryFeeCalc.new_entry_price?.toLocaleString('hu-HU')} Ft</span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-slate-600">Aktuális fizetendő összesen:</span>
                            <span data-testid="fee-new-total" className="font-bold text-emerald-800">{entryFeeCalc.new_total?.toLocaleString('hu-HU')} Ft</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-3">
                        Díjszabás: 1-{entryFeeCalc.tier1_limit} start: {entryFeeCalc.tier1_price?.toLocaleString('hu-HU')} Ft/start • {entryFeeCalc.tier1_limit + 1}. starttól: {entryFeeCalc.tier2_price?.toLocaleString('hu-HU')} Ft/start
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => setShowEntryModal(false)} className="flex-1">Mégse</Button>
                    <Button data-testid="submit-entry-btn" onClick={handleEntry} disabled={!selectedHorseId} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Nevezés</Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Kezdési idő beállítás Modal */}
        <AnimatePresence>
          {showStartTimeModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">Kezdési idő beállítása</h3>
                  <button onClick={() => setShowStartTimeModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock size={20} className="text-purple-600" />
                      <span className="font-medium text-purple-800">Első induló kezdési ideje</span>
                    </div>
                    <p className="text-sm text-purple-600 mb-3">
                      Ez az idő az első indulóra vonatkozik. A többi induló ideje automatikusan számolódik (2 perc/induló).
                    </p>
                    <input
                      type="time"
                      value={eventStartTime}
                      onChange={(e) => setEventStartTime(e.target.value)}
                      className="w-full border border-purple-300 rounded-lg px-4 py-3 text-lg text-slate-800 bg-white"
                    />
                  </div>

                  {/* Előnézet */}
                  {eventStartTime && startlist.length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700 mb-2">Előnézet:</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {startlist.slice(0, 5).map((entry, idx) => {
                          const [h, m] = eventStartTime.split(':').map(Number);
                          const totalMinutes = h * 60 + m + (idx * 2);
                          const hours = Math.floor(totalMinutes / 60);
                          const mins = totalMinutes % 60;
                          const time = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                          return (
                            <div key={entry.id} className="flex justify-between text-sm">
                              <span className="text-slate-600">{idx + 1}. {entry.rider_name}</span>
                              <span className="font-medium text-slate-800">{time}</span>
                            </div>
                          );
                        })}
                        {startlist.length > 5 && (
                          <p className="text-xs text-slate-500 mt-1">... és még {startlist.length - 5} induló</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-6 border-t mt-6">
                  <Button variant="outline" onClick={() => setShowStartTimeModal(false)} className="flex-1">Mégse</Button>
                  <Button 
                    onClick={async () => {
                      await saveEventStartTime();
                      setShowStartTimeModal(false);
                    }} 
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Save size={18} className="mr-2" />Mentés
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Pályarajz feltöltés Modal */}
        <AnimatePresence>
          {showCourseMapModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">Pályarajz feltöltése</h3>
                  <button onClick={() => setShowCourseMapModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText size={20} className="text-teal-600" />
                      <span className="font-medium text-teal-800">PDF fájl feltöltése</span>
                    </div>
                    <p className="text-sm text-teal-600 mb-4">
                      Válaszd ki a pályarajz PDF fájlt. Maximum 10MB méretű fájl tölthető fel.
                    </p>
                    
                    {event?.course_map_url && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-teal-200">
                        <p className="text-sm text-slate-600 mb-2">Jelenlegi pályarajz:</p>
                        <a 
                          href={`${BACKEND_URL}${event.course_map_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:text-teal-800 underline text-sm"
                        >
                          Megtekintés
                        </a>
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleCourseMapUpload}
                      disabled={courseMapUploading}
                      className="w-full border border-teal-300 rounded-lg px-4 py-3 text-slate-800 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-600 file:text-white file:cursor-pointer hover:file:bg-teal-700"
                    />
                    
                    {courseMapUploading && (
                      <div className="flex items-center gap-2 mt-3 text-teal-600">
                        <div className="animate-spin w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full"></div>
                        <span className="text-sm">Feltöltés...</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-6 border-t mt-6">
                  <Button variant="outline" onClick={() => setShowCourseMapModal(false)} className="flex-1">Mégse</Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>


        <UserManagementModal />
        <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} onLoginSuccess={(userData, userToken) => { setUser(userData); setToken(userToken); localStorage.setItem("comp_token", userToken); setShowAuthModal(false); }} />
      </div>
    );
  }

  // ==================== RENDER: COMPETITION PAGE ====================
  if (competitionId && competition) {
    const groupedEvents = getEventsByCategory(competition.events);
    
    return (
      <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <button onClick={() => navigate("/verseny")} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
              <ArrowLeft size={20} /><span className="font-medium">Vissza</span>
            </button>
            <div className="flex items-center gap-3">
              {(canManageCompetition() || canEnterResults()) && (
                <Button data-testid="edit-competition-btn" onClick={() => { setEditForm({...competition}); setShowEditModal(true); }} variant="outline" size="sm">
                  <Settings size={16} className="mr-1" />
                  {canManageCompetition() ? 'Szerkesztés' : 'Versenyszámok'}
                </Button>
              )}
              {userMenuEl}
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
            <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${getCompetitionColor(competition)}`}>{competition.name}</h1>
            <div className="flex flex-wrap gap-4 text-slate-600 mb-4">
              <span className="flex items-center gap-2"><Calendar size={18} className="text-blue-600" />{formatDate(competition.date)}</span>
              {competition.start_time && <span className="flex items-center gap-2"><Clock size={18} className="text-blue-600" />Kezdés: {competition.start_time}</span>}
            </div>
            {competition.description && <p className="text-slate-600 mb-4">{competition.description}</p>}
            
            {/* Hivatalos személyek */}
            {(() => {
              const ROLE_DISPLAY = [
                ["vezetobiro", "Vezetőbíró", "official-vezetobiro"], ["judges", "Bíró(k)", "official-judges"], ["stilusbiro", "Stílusbíró", "official-stilusbiro"],
                ["dijlovasbiro", "Díjlovas bíró", "official-dijlovasbiro"], ["lovastusabiro", "Lovastusa bíró", "official-lovastusabiro"], ["melegitobiro", "Melegítőpálya bíró", "official-melegitobiro"],
                ["directors", "Versenyszervező", "official-directors"], ["course_builders", "Pályaépítő", "official-course-builders"], ["versenyiroda", "Versenyiroda", "official-versenyiroda"],
              ];
              const names = competition.official_names || {};
              const rows = ROLE_DISPLAY.filter(([k]) => names[k]?.length > 0);
              if (rows.length === 0) return null;
              return (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h3 data-testid="officials-display-heading" className="text-sm font-semibold text-slate-700 mb-2">Hivatalos személyek</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    {rows.map(([k, label, tid]) => (
                      <div key={k}>
                        <span className="text-slate-500">{label}:</span>
                        <span data-testid={tid} className="ml-2 text-slate-800">{names[k].join(", ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Nevezési díj információ */}
            {competition.entry_fee_enabled && (
              <div data-testid="competition-entry-fee-info" className="border-t border-slate-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Nevezési díj</h3>
                <div className="bg-emerald-50 rounded-lg p-3 text-sm">
                  <p className="text-emerald-800">
                    <span className="font-medium">1-{competition.entry_fee_tier1_limit || 2} start:</span> {(competition.entry_fee_tier1_price || 3000).toLocaleString('hu-HU')} Ft/start
                  </p>
                  <p className="text-emerald-700">
                    <span className="font-medium">{(competition.entry_fee_tier1_limit || 2) + 1}. starttól:</span> {(competition.entry_fee_tier2_price || 2500).toLocaleString('hu-HU')} Ft/start
                  </p>
                </div>
              </div>
            )}
            
            {/* Résztvevők és Fizetések gombok */}
            <div className="border-t border-slate-200 pt-4 mt-4 flex flex-wrap gap-3">
              <Button
                data-testid="participants-button"
                onClick={() => {
                  setShowParticipantsModal(true);
                  setParticipantsView("select");
                  setSelectedParticipant(null);
                  fetchParticipants(competitionId);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Users size={18} className="mr-2" />
                Résztvevők
              </Button>
              
              {canViewPayments() && (
                <Button
                  data-testid="payments-button"
                  onClick={() => {
                    setShowPaymentsModal(true);
                    fetchPayments(competitionId);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Banknote size={18} className="mr-2" />
                  Fizetések
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-8">
            {(() => {
              // Calculate global event numbering across all categories
              let globalEventNumber = 0;
              const eventNumbers = {};
              CATEGORY_ORDER.forEach(category => {
                const categoryEvents = groupedEvents[category];
                if (categoryEvents?.length) {
                  categoryEvents.forEach(ev => {
                    globalEventNumber++;
                    eventNumbers[ev.id] = globalEventNumber;
                  });
                }
              });
              
              return CATEGORY_ORDER.map(category => {
                const categoryEvents = groupedEvents[category];
                if (!categoryEvents?.length) return null;
                
                return (
                  <div key={category}>
                    <div className="flex items-center gap-4 mb-4">
                      <h2 className="text-xl font-bold text-slate-800">{category}</h2>
                      <div className="flex-1 h-px bg-slate-200"></div>
                    </div>
                    
                    <div className="space-y-3">
                      {categoryEvents.map(ev => (
                        <button key={ev.id} onClick={() => !ev.cancelled && navigate(eventPath(competition, ev))} className={`w-full bg-white rounded-xl shadow-sm hover:shadow-md p-4 flex items-center justify-between group ${ev.cancelled ? 'opacity-70 cursor-not-allowed' : ''}`}>
                          <div className="text-left">
                            <p className={`font-semibold ${ev.cancelled ? 'text-red-600' : getEventColor(ev)}`}>
                              <span className="text-slate-500 font-normal mr-1">{eventNumbers[ev.id]})</span>
                              {ev.cancelled ? `E L M A R A D - ${ev.name}` : ev.name}
                            </p>
                            {ev.height && !ev.cancelled && <p className="text-sm text-slate-500 ml-6">{ev.height}</p>}
                            {ev.is_free && !ev.cancelled && <span className="text-xs text-green-600 ml-6">Díjmentes</span>}
                            {!ev.is_free && ev.custom_entry_fee && !ev.cancelled && <span className="text-xs text-blue-600 ml-6">{ev.custom_entry_fee} Ft/start</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            {!ev.cancelled && <span className="text-sm text-slate-500">{ev.entry_count || 0} start</span>}
                            <ChevronRight size={20} className="text-slate-400 group-hover:text-blue-600" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </main>

        {/* Edit Competition Modal */}
        <AnimatePresence>
          {showEditModal && editForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800">{isOnlyJudge() ? 'Versenyszámok szerkesztése' : 'Verseny szerkesztése'}</h3>
                  <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                </div>

                <div className="space-y-4">
                  {/* Verseny adatok - NEM látható bíróknak */}
                  {!isOnlyJudge() && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Verseny neve</label>
                        <input type="text" value={editForm.name || ""} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Dátum</label>
                          <input type="date" value={editForm.date || ""} onChange={(e) => setEditForm({...editForm, date: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Kezdés</label>
                          <input type="time" value={editForm.start_time || ""} onChange={(e) => setEditForm({...editForm, start_time: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Nevezési határidő</label>
                        <input type="datetime-local" value={editForm.entry_deadline || ""} onChange={(e) => setEditForm({...editForm, entry_deadline: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Leírás</label>
                        <textarea value={editForm.description || ""} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 h-24" />
                      </div>
                      
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-slate-800 mb-3">Hivatalos személyek (LAUSZ)</h4>
                        <p className="text-sm text-slate-500 mb-3">Válassz a listából - a kiválasztott személyek email értesítést kapnak</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Vezetőbíró</label>
                            <select 
                              multiple
                              value={editForm.vezetobiro_ids || []} 
                              onChange={(e) => setEditForm({...editForm, vezetobiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.vezetobiro?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Bíró(k)</label>
                            <select 
                              multiple
                              data-testid="edit-judge-select"
                              value={editForm.judge_ids || []} 
                              onChange={(e) => setEditForm({...editForm, judge_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.judges?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Stílusbíró(k)</label>
                            <select 
                              multiple
                              value={editForm.stilusbiro_ids || []} 
                              onChange={(e) => setEditForm({...editForm, stilusbiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.stilusbiro?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Díjlovas bíró(k)</label>
                            <select 
                              multiple
                              value={editForm.dijlovasbiro_ids || []} 
                              onChange={(e) => setEditForm({...editForm, dijlovasbiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.dijlovasbiro?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Lovastusa bíró(k)</label>
                            <select 
                              multiple
                              value={editForm.lovastusabiro_ids || []} 
                              onChange={(e) => setEditForm({...editForm, lovastusabiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.lovastusabiro?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Melegítőpálya bíró(k)</label>
                            <select 
                              multiple
                              value={editForm.melegitobiro_ids || []} 
                              onChange={(e) => setEditForm({...editForm, melegitobiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.melegitobiro?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Versenyszervező(k)</label>
                            <select 
                              multiple
                              data-testid="edit-director-select"
                              value={editForm.director_ids || []} 
                              onChange={(e) => setEditForm({...editForm, director_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.organizers?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Pályaépítő(k)</label>
                            <select 
                              multiple
                              data-testid="edit-course-builder-select"
                              value={editForm.course_builder_ids || []} 
                              onChange={(e) => setEditForm({...editForm, course_builder_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.course_builders?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Versenyiroda</label>
                            <select 
                              multiple
                              value={editForm.versenyiroda_ids || []} 
                              onChange={(e) => setEditForm({...editForm, versenyiroda_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                              className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                            >
                              {roleUsers.versenyiroda?.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Ctrl+klikk több személy kiválasztásához</p>
                      </div>
                      
                      {/* Nevezési díj szekció a szerkesztéshez */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center gap-3 mb-3">
                          <input 
                            type="checkbox" 
                            id="entry-fee-enabled-edit"
                            data-testid="entry-fee-enabled-edit"
                            checked={editForm.entry_fee_enabled || false}
                            onChange={(e) => setEditForm({...editForm, entry_fee_enabled: e.target.checked})}
                            className="w-5 h-5 rounded border-slate-300"
                          />
                          <label htmlFor="entry-fee-enabled-edit" className="font-semibold text-slate-800 cursor-pointer">Nevezési díj</label>
                        </div>
                        
                        {editForm.entry_fee_enabled && (
                          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                            <p className="text-sm text-emerald-700 mb-4">
                              Állítsd be a sávos díjazást: más ár az első néhány startnál, kedvezményes ár több start esetén.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Első sáv határa (start)</label>
                                <input 
                                  type="number" 
                                  min="1"
                                  data-testid="entry-fee-tier1-limit-edit"
                                  value={editForm.entry_fee_tier1_limit || 2}
                                  onChange={(e) => setEditForm({...editForm, entry_fee_tier1_limit: parseInt(e.target.value) || 2})}
                                  className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                                  placeholder="pl. 2"
                                />
                                <p className="text-xs text-slate-500 mt-1">Ennyi startig érvényes az első sáv díja</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Első sáv díja (Ft/start)</label>
                                <input 
                                  type="number" 
                                  min="0"
                                  step="100"
                                  data-testid="entry-fee-tier1-price-edit"
                                  value={editForm.entry_fee_tier1_price || 3000}
                                  onChange={(e) => setEditForm({...editForm, entry_fee_tier1_price: parseInt(e.target.value) || 0})}
                                  className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                                  placeholder="pl. 3000"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1">Kedvezményes díj (Ft/start)</label>
                                <input 
                                  type="number" 
                                  min="0"
                                  step="100"
                                  data-testid="entry-fee-tier2-price-edit"
                                  value={editForm.entry_fee_tier2_price || 2500}
                                  onChange={(e) => setEditForm({...editForm, entry_fee_tier2_price: parseInt(e.target.value) || 0})}
                                  className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                                  placeholder="pl. 2500"
                                />
                                <p className="text-xs text-slate-500 mt-1">{editForm.entry_fee_tier1_limit || 2}. start felett</p>
                              </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                              <p className="text-sm text-slate-700">
                                <strong>Példa:</strong> 1-{editForm.entry_fee_tier1_limit || 2} start: <span className="text-emerald-600 font-semibold">{(editForm.entry_fee_tier1_price || 3000).toLocaleString('hu-HU')} Ft/start</span>, {(editForm.entry_fee_tier1_limit || 2) + 1}. starttól: <span className="text-emerald-600 font-semibold">{(editForm.entry_fee_tier2_price || 2500).toLocaleString('hu-HU')} Ft/start</span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* Versenyszámok szerkesztése */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800">Versenyszámok</h4>
                      <Button variant="outline" size="sm" onClick={() => setEditForm({...editForm, events: [...(editForm.events || []), { id: `new-${Date.now()}`, name: "", category: "Díjugratás", judging_type: "hiba_ido", height: "" }]})}>
                        <Plus size={16} className="mr-1" />Hozzáad
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {(editForm.events || []).map((ev, idx) => (
                        <div key={ev.id || idx} className="bg-slate-50 p-4 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium text-slate-700">#{idx + 1}</span>
                            <button onClick={() => { const newEvents = editForm.events.filter((_, i) => i !== idx); setEditForm({...editForm, events: newEvents}); }} className="text-red-500 hover:text-red-700">
                              <X size={20} />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <input 
                              type="text" 
                              value={ev.name || ""} 
                              onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].name = e.target.value; setEditForm({...editForm, events: newEvents}); }} 
                              className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" 
                              placeholder="Versenyszám neve" 
                            />
                            <select 
                              value={ev.category || "Díjugratás"} 
                              onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].category = e.target.value; setEditForm({...editForm, events: newEvents}); }} 
                              className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white"
                            >
                              {CATEGORY_ORDER.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                            <select 
                              value={ev.judging_type || "hiba_ido"} 
                              onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].judging_type = e.target.value; setEditForm({...editForm, events: newEvents}); }} 
                              className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white"
                            >
                              {Object.entries(JUDGING_TYPES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                            <input 
                              type="text" 
                              value={ev.height || ""} 
                              onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].height = e.target.value; setEditForm({...editForm, events: newEvents}); }} 
                              className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" 
                              placeholder="Magasság (pl. 100 cm)" 
                            />
                          </div>
                          {/* Conditional fields based on judging type */}
                          {ev.judging_type === 'alapido' && (
                            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <label className="block text-sm font-medium text-amber-800 mb-1">Alapidő (másodperc) *</label>
                              <input 
                                type="number" 
                                value={ev.base_time || ""} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].base_time = parseInt(e.target.value) || null; setEditForm({...editForm, events: newEvents}); }} 
                                className="w-full border border-amber-300 rounded-lg p-2 text-slate-800 bg-white" 
                                placeholder="Kötelező! pl. 65" 
                                required
                              />
                              <p className="text-xs text-amber-600 mt-1">+1 hibapont minden megkezdett 2 mp túllépés után</p>
                            </div>
                          )}
                          {ev.judging_type === 'dijlovaglas' && (
                            <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                              <label className="block text-sm font-medium text-indigo-800 mb-1">Maximum pontszám *</label>
                              <input 
                                type="number" 
                                value={ev.max_points || ""} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].max_points = parseInt(e.target.value) || null; setEditForm({...editForm, events: newEvents}); }} 
                                className="w-full border border-indigo-300 rounded-lg p-2 text-slate-800 bg-white" 
                                placeholder="Kötelező! pl. 180" 
                                required
                              />
                              <p className="text-xs text-indigo-600 mt-1">A százalék automatikusan számolódik</p>
                            </div>
                          )}
                          {ev.judging_type === 'idealis_ido' && (
                            <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                              <label className="block text-sm font-medium text-teal-800 mb-1">Ideális idő (másodperc) *</label>
                              <input 
                                type="number" 
                                value={ev.ideal_time || ev.base_time || ""} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].ideal_time = parseInt(e.target.value) || null; setEditForm({...editForm, events: newEvents}); }} 
                                className="w-full border border-teal-300 rounded-lg p-2 text-slate-800 bg-white" 
                                placeholder="Kötelező! pl. 90" 
                                required
                              />
                              <p className="text-xs text-teal-600 mt-1">Az ideális időhöz legközelebbi idő nyer</p>
                            </div>
                          )}
                          {(ev.judging_type === 'stilus' || ev.judging_type === 'caprilli' || ev.judging_type === 'pontgyujto' || ev.judging_type === 'fokozatos') && (
                            <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                              <label className="block text-sm font-medium text-purple-800 mb-1">Maximum pontszám (opcionális)</label>
                              <input 
                                type="number" 
                                value={ev.max_points || ""} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].max_points = parseInt(e.target.value) || null; setEditForm({...editForm, events: newEvents}); }} 
                                className="w-full border border-purple-300 rounded-lg p-2 text-slate-800 bg-white" 
                                placeholder="pl. 100" 
                              />
                              <p className="text-xs text-purple-600 mt-1">
                                {ev.judging_type === 'stilus' && "Ló-lovas harmónia, ülés, segítségadás értékelése"}
                                {ev.judging_type === 'caprilli' && "Ló idomítottsága, lovas ülése pontozása"}
                                {ev.judging_type === 'pontgyujto' && "Akadályokért pontok járnak, legtöbb pont nyer"}
                                {ev.judging_type === 'fokozatos' && "Akadályok egyre több pontot érnek"}
                              </p>
                            </div>
                          )}
                          {ev.judging_type === 'vadaszugratas' && (
                            <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                              <p className="text-sm text-orange-800">
                                <strong>C-szabály:</strong> Hibák másodpercekké válnak (+4 mp/hiba), összidő dönt.
                              </p>
                            </div>
                          )}
                          {ev.judging_type === 'hunter' && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-sm text-green-800">
                                <strong>LAUSZ Hunter:</strong> Stílus + akadályteljesítés kombinált értékelése.
                              </p>
                            </div>
                          )}
                          {ev.judging_type === 'tajekozdasi' && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-800">
                                <strong>Tájékozódási:</strong> Útvonal követése térképpel, pontosság és idő alapján.
                              </p>
                            </div>
                          )}
                          {/* Show judging type description for other types */}
                          {ev.judging_type && JUDGING_DESCRIPTIONS[ev.judging_type] && !['alapido', 'dijlovaglas', 'idealis_ido', 'stilus', 'caprilli', 'pontgyujto', 'fokozatos', 'vadaszugratas', 'hunter', 'tajekozdasi'].includes(ev.judging_type) && (
                            <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                              <p className="text-xs text-slate-600">{JUDGING_DESCRIPTIONS[ev.judging_type]}</p>
                            </div>
                          )}
                          
                          {/* Ranglista pontszámítás beállítás */}
                          <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                            {/* Elmaradt versenyszám */}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={ev.cancelled || false} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].cancelled = e.target.checked; setEditForm({...editForm, events: newEvents}); }}
                                className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500"
                              />
                              <span className="text-sm text-red-600 font-medium">Elmaradt versenyszám</span>
                            </label>
                            
                            {/* Ranglista */}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={ev.counts_for_ranking !== false} 
                                onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].counts_for_ranking = e.target.checked; setEditForm({...editForm, events: newEvents}); }}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">Számít a ranglistába</span>
                            </label>
                            
                            {/* Egyedi nevezési díj */}
                            <div className="bg-slate-50 rounded-lg p-3">
                              <label className="flex items-center gap-2 cursor-pointer mb-2">
                                <input 
                                  type="checkbox" 
                                  checked={ev.is_free || false} 
                                  onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].is_free = e.target.checked; if (e.target.checked) newEvents[idx].custom_entry_fee = null; setEditForm({...editForm, events: newEvents}); }}
                                  className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500"
                                />
                                <span className="text-sm text-green-700">Díjmentes versenyszám</span>
                              </label>
                              {!ev.is_free && (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-600">Egyedi díj:</span>
                                  <input 
                                    type="number" 
                                    value={ev.custom_entry_fee || ""} 
                                    onChange={(e) => { const newEvents = [...editForm.events]; newEvents[idx].custom_entry_fee = e.target.value ? parseInt(e.target.value) : null; setEditForm({...editForm, events: newEvents}); }}
                                    className="w-24 border border-slate-200 rounded px-2 py-1 text-sm"
                                    placeholder="alapért."
                                  />
                                  <span className="text-sm text-slate-500">Ft</span>
                                </div>
                              )}
                              <p className="text-xs text-slate-500 mt-1">Üresen hagyva a verseny alapértelmezett díja érvényes</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Verseny elmarad / Törlés */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-800 mb-3">Verseny kezelése</h4>
                    <div className="flex gap-3">
                      <Button onClick={handleCancelCompetition} variant="outline" className="flex-1 text-orange-600 border-orange-300 hover:bg-orange-50">
                        Verseny elmarad
                      </Button>
                      <Button onClick={handleDeleteCompetition} variant="outline" className="flex-1 text-red-600 border-red-300 hover:bg-red-50">
                        Törlés
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-6 border-t mt-6">
                  <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Mégse</Button>
                  <Button data-testid="submit-edit-competition-btn" onClick={handleUpdateCompetition} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Mentés</Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Résztvevők Modal */}
        <AnimatePresence>
          {showParticipantsModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
              >
                {/* Header */}
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {participantsView !== "select" && (
                      <button
                        onClick={() => {
                          if (selectedParticipant) {
                            setSelectedParticipant(null);
                          } else {
                            setParticipantsView("select");
                          }
                        }}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <ArrowLeft size={20} className="text-slate-600" />
                      </button>
                    )}
                    <h2 className="text-lg font-bold text-slate-800">
                      {participantsView === "select" && "Résztvevők"}
                      {participantsView === "riders" && !selectedParticipant && "Lovasok"}
                      {participantsView === "horses" && !selectedParticipant && "Lovak"}
                      {selectedParticipant && selectedParticipant.name}
                    </h2>
                    {selectedParticipant && (
                      <span className="text-sm text-slate-500">
                        ({selectedParticipant.entries?.length || 0} start)
                      </span>
                    )}
                  </div>
                  <button onClick={() => setShowParticipantsModal(false)} className="p-1 hover:bg-slate-100 rounded">
                    <X size={20} className="text-slate-600" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {/* Selection view */}
                  {participantsView === "select" && (
                    <div className="space-y-3">
                      <button
                        data-testid="view-riders-btn"
                        onClick={() => setParticipantsView("riders")}
                        className="w-full p-4 bg-blue-50 hover:bg-blue-100 rounded-xl flex items-center justify-between group transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <User size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-slate-800">Lovasok</p>
                            <p className="text-sm text-slate-500">{participantsData.riders.length} lovas</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-slate-400 group-hover:text-blue-600" />
                      </button>
                      
                      <button
                        data-testid="view-horses-btn"
                        onClick={() => setParticipantsView("horses")}
                        className="w-full p-4 bg-amber-50 hover:bg-amber-100 rounded-xl flex items-center justify-between group transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-lg">🐴</span>
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-slate-800">Lovak</p>
                            <p className="text-sm text-slate-500">{participantsData.horses.length} ló</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-slate-400 group-hover:text-amber-600" />
                      </button>
                    </div>
                  )}

                  {/* Riders list */}
                  {participantsView === "riders" && !selectedParticipant && (
                    <div className="space-y-2">
                      {participantsData.riders.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">Nincs nevezett lovas</p>
                      ) : (
                        participantsData.riders.map((rider, index) => (
                          <button
                            key={rider.id}
                            data-testid={`rider-item-${index}`}
                            onClick={() => setSelectedParticipant({ type: "rider", ...rider })}
                            className="w-full p-3 bg-slate-50 hover:bg-blue-50 rounded-lg flex items-center justify-between group transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-slate-400 w-6">{index + 1}.</span>
                              <span className="font-medium text-slate-800">{rider.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">{rider.entries.length} start</span>
                              <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-600" />
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* Horses list */}
                  {participantsView === "horses" && !selectedParticipant && (
                    <div className="space-y-2">
                      {participantsData.horses.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">Nincs nevezett ló</p>
                      ) : (
                        participantsData.horses.map((horse, index) => (
                          <button
                            key={horse.id}
                            data-testid={`horse-item-${index}`}
                            onClick={() => setSelectedParticipant({ type: "horse", ...horse })}
                            className="w-full p-3 bg-slate-50 hover:bg-amber-50 rounded-lg flex items-center justify-between group transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-slate-400 w-6">{index + 1}.</span>
                              <span className="font-medium text-slate-800">{horse.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">{horse.entries.length} start</span>
                              <ChevronRight size={16} className="text-slate-400 group-hover:text-amber-600" />
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* Selected participant details */}
                  {selectedParticipant && (
                    <div className="space-y-3">
                      {(() => {
                        // Sort entries by event number
                        const sortedEntries = [...(selectedParticipant.entries || [])].sort((a, b) => {
                          // First by event_number if available
                          if (a.event_number !== undefined && b.event_number !== undefined) {
                            return a.event_number - b.event_number;
                          }
                          // Fallback to event name
                          return (a.event_name || "").localeCompare(b.event_name || "", 'hu');
                        });
                        
                        // Check if current user can manage this participant's entries
                        const canManageParticipant = (entry) => {
                          if (!user) return false;
                          // Get the rider ID from the entry
                          const riderId = entry.rider_id || entry.user_id;
                          // Own account
                          if (user.id === riderId) return true;
                          // Linked account
                          if (linkedAccounts.some(la => la.id === riderId)) return true;
                          // Admin can manage all
                          if (user.role === 'admin') return true;
                          return false;
                        };
                        
                        const handleDeleteEntry = async (entry) => {
                          if (!window.confirm(`Biztosan törölni szeretnéd ezt a nevezést?\n${entry.event_name}`)) return;
                          
                          try {
                            await axios.delete(`${API}/competition/entries/${entry.id}?token=${token}`);
                            toast.success("Nevezés törölve");
                            // Refresh participants data
                            fetchParticipants(competitionId);
                            // Update selected participant entries
                            const updatedEntries = selectedParticipant.entries.filter(e => e.id !== entry.id);
                            if (updatedEntries.length === 0) {
                              setSelectedParticipant(null);
                            } else {
                              setSelectedParticipant({...selectedParticipant, entries: updatedEntries});
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Hiba a nevezés törlésekor");
                          }
                        };
                        
                        return sortedEntries.map((entry, index) => (
                          <div
                            key={entry.id || index}
                            className={`p-3 rounded-lg border ${
                              selectedParticipant.type === "rider" ? "bg-blue-50 border-blue-200" : "bg-amber-50 border-amber-200"
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded font-medium">
                                    {entry.event_number || index + 1})
                                  </span>
                                  <p className="font-semibold text-slate-800">{entry.event_name}</p>
                                </div>
                                <div className="mt-2 text-sm text-slate-600 space-y-1">
                                  {selectedParticipant.type === "rider" ? (
                                    <>
                                      <p>Indulási sorszám: <span className="font-medium">{entry.start_number || "N/A"}</span></p>
                                      <p>Ló: <span className="font-medium">{entry.horse_name}</span></p>
                                    </>
                                  ) : (
                                    <>
                                      <p>Lovas: <span className="font-medium">{entry.user_name}</span></p>
                                      <p>Indulási sorszám: <span className="font-medium">{entry.start_number || "N/A"}</span></p>
                                    </>
                                  )}
                                </div>
                              </div>
                              {canManageParticipant(entry) && (
                                <button
                                  onClick={() => handleDeleteEntry(entry)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Nevezés törlése"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Fizetések Modal */}
        <AnimatePresence>
          {showPaymentsModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
              >
                {/* Header */}
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                      <Banknote size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">Fizetések</h2>
                      {paymentsData.entry_fee_enabled && (
                        <p className="text-sm text-slate-500">
                          1-{paymentsData.tier1_limit} start: {paymentsData.tier1_price?.toLocaleString('hu-HU')} Ft • 
                          {paymentsData.tier1_limit + 1}. starttól: {paymentsData.tier2_price?.toLocaleString('hu-HU')} Ft
                        </p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setShowPaymentsModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X size={20} className="text-slate-600" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-4">
                  {paymentsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    </div>
                  ) : paymentsData.payments.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      Nincs nevezett lovas ezen a versenyen
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">#</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">Lovas neve</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">Versenyszámok</th>
                            <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Fizetendő</th>
                            <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Fizetett</th>
                            <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Hátralék</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsData.payments.map((payment, index) => (
                            <tr 
                              key={payment.rider_id}
                              className={`border-b transition-colors ${
                                payment.is_paid 
                                  ? 'bg-emerald-50 hover:bg-emerald-100' 
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <td className="p-3 text-sm text-slate-600 align-top">
                                {index + 1}.
                              </td>
                              <td className="p-3 align-top">
                                <span className={`font-medium ${payment.is_paid ? 'text-emerald-700' : 'text-slate-800'}`}>
                                  {payment.rider_name}
                                </span>
                                <span className="text-xs text-slate-500 ml-2">
                                  ({payment.total_starts} start)
                                </span>
                              </td>
                              <td className="p-3 align-top">
                                <div className="space-y-1">
                                  {payment.entries.map((entry, eIdx) => (
                                    <div key={eIdx} className="text-sm text-slate-600">
                                      {entry.event_number}. {entry.event_name}
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td className={`p-3 text-right align-top font-medium ${payment.is_paid ? 'text-emerald-700' : 'text-slate-800'}`}>
                                {payment.amount_due.toLocaleString('hu-HU')} Ft
                              </td>
                              <td className="p-3 text-right align-top">
                                <div className="flex items-center justify-end gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={payment.amount_paid}
                                    onChange={(e) => {
                                      // Update local state immediately
                                      const newValue = parseInt(e.target.value) || 0;
                                      setPaymentsData(prev => ({
                                        ...prev,
                                        payments: prev.payments.map(p => 
                                          p.rider_id === payment.rider_id 
                                            ? {...p, amount_paid: newValue, is_paid: newValue >= p.amount_due, amount_remaining: Math.max(0, p.amount_due - newValue)}
                                            : p
                                        )
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      // Save to backend on blur
                                      updatePayment(payment.rider_id, e.target.value);
                                    }}
                                    className={`w-24 px-2 py-1 text-right border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                      payment.is_paid ? 'bg-emerald-100 border-emerald-300' : 'border-slate-300'
                                    }`}
                                  />
                                  <span className="text-sm text-slate-500">Ft</span>
                                  {savingPayment === payment.rider_id && (
                                    <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                  )}
                                </div>
                              </td>
                              <td className={`p-3 text-right align-top font-medium ${
                                payment.amount_remaining > 0 ? 'text-red-600' : 'text-emerald-600'
                              }`}>
                                {payment.amount_remaining > 0 
                                  ? `${payment.amount_remaining.toLocaleString('hu-HU')} Ft`
                                  : '✓ Rendezve'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-100 font-semibold">
                            <td colSpan="3" className="p-3 text-right text-slate-700">Összesen:</td>
                            <td className="p-3 text-right text-slate-800">
                              {paymentsData.payments.reduce((sum, p) => sum + p.amount_due, 0).toLocaleString('hu-HU')} Ft
                            </td>
                            <td className="p-3 text-right text-emerald-700">
                              {paymentsData.payments.reduce((sum, p) => sum + p.amount_paid, 0).toLocaleString('hu-HU')} Ft
                            </td>
                            <td className="p-3 text-right text-red-600">
                              {paymentsData.payments.reduce((sum, p) => sum + p.amount_remaining, 0).toLocaleString('hu-HU')} Ft
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-emerald-100 rounded"></div>
                        <span className="text-slate-600">Kifizetve</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-white border border-slate-200 rounded"></div>
                        <span className="text-slate-600">Hátralékos</span>
                      </div>
                    </div>
                    <p className="text-slate-500">
                      A fizetett összegek automatikusan mentődnek
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <UserManagementModal />
        <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} onLoginSuccess={(userData, userToken) => { setUser(userData); setToken(userToken); localStorage.setItem("comp_token", userToken); setShowAuthModal(false); }} />
      </div>
    );
  }

  // ==================== RENDER: COMPETITIONS LIST ====================
  const filteredCompetitions = getFilteredCompetitions();
  
  return (
    <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
            </div>
            <span className="font-bold text-xl text-slate-800">Versenyek</span>
          </a>
          {userMenuEl}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Top Navigation Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button 
            onClick={() => setActiveTab("versenyek")} 
            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all ${activeTab === "versenyek" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            Versenyek
          </button>
          <button 
            onClick={() => setActiveTab("ranglista")} 
            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all ${activeTab === "ranglista" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            Ranglista
          </button>
          <button 
            onClick={() => setActiveTab("lovas-lo")} 
            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all ${activeTab === "lovas-lo" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            Lovas/Ló
          </button>
          <button 
            onClick={() => setActiveTab("elmult")} 
            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all ${activeTab === "elmult" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            Elmúlt versenyek
          </button>
        </div>

        {/* Ranglista Tab - Empty for now */}
        {activeTab === "ranglista" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <Trophy size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Ranglista</h2>
            <p className="text-slate-500">Hamarosan elérhető...</p>
          </div>
        )}

        {/* Lovas/Ló Tab - Empty for now */}
        {activeTab === "lovas-lo" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Lovas/Ló keresés</h2>
            <p className="text-slate-500">Hamarosan elérhető...</p>
          </div>
        )}

        {/* Versenyek / Elmúlt versenyek Tab */}
        {(activeTab === "versenyek" || activeTab === "elmult") && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-slate-800">
                {activeTab === "elmult" ? "Elmúlt versenyek" : "Versenyek"}
              </h1>
              {activeTab === "versenyek" && canCreateCompetition() && (
                <Button data-testid="create-competition-btn" onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={18} className="mr-2" />Új verseny
                </Button>
              )}
            </div>

            {filteredCompetitions.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <Trophy size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">
                  {activeTab === "elmult" ? "Még nincsenek elmúlt versenyek." : "Jelenleg nincs meghirdetett verseny."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCompetitions.map(comp => (
                  <button key={comp.id} onClick={() => navigate(compPath(comp))} className={`w-full bg-white rounded-2xl shadow-sm hover:shadow-lg p-6 flex items-center justify-between group ${comp.status === "cancelled" || comp.status === "elmaradt" ? "border-2 border-red-200" : ""}`}>
                    <div className="text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`text-lg font-bold ${getCompetitionColor(comp)}`}>{comp.name}</h3>
                        {(comp.status === "cancelled" || comp.status === "elmaradt") && (
                          <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-semibold">Elmaradt</span>
                        )}
                      </div>
                      <p className="text-slate-500 flex items-center gap-2"><Calendar size={16} />{formatDate(comp.date)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Összesen</p>
                        <p className="font-semibold text-slate-800">{getTotalEntries(comp)} nevezés</p>
                      </div>
                      <ChevronRight size={24} className="text-slate-400 group-hover:text-blue-600" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Create Competition Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">Új verseny létrehozása</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Verseny neve *</label>
                  <input data-testid="create-comp-name-input" type="text" value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" placeholder="pl. Nyárzáró Háziverseny" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Dátum *</label>
                    <input data-testid="create-comp-date-input" type="date" value={createForm.date} onChange={(e) => setCreateForm({...createForm, date: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Kezdés</label>
                    <input type="time" value={createForm.start_time} onChange={(e) => setCreateForm({...createForm, start_time: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Nevezési határidő</label>
                  <input type="datetime-local" value={createForm.entry_deadline} onChange={(e) => setCreateForm({...createForm, entry_deadline: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Leírás</label>
                  <textarea value={createForm.description} onChange={(e) => setCreateForm({...createForm, description: e.target.value})} className="w-full border border-slate-200 rounded-lg p-3 h-24" placeholder="Verseny leírása..." />
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-800 mb-3">Hivatalos személyek (LAUSZ)</h4>
                  <p className="text-sm text-slate-500 mb-3">Válassz a listából - a kiválasztott személyek email értesítést kapnak</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Vezetőbíró</label>
                      <select 
                        multiple
                        value={createForm.vezetobiro_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, vezetobiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.vezetobiro?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Bíró(k)</label>
                      <select 
                        multiple
                        data-testid="create-judge-select"
                        value={createForm.judge_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, judge_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.judges?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Stílusbíró(k)</label>
                      <select 
                        multiple
                        value={createForm.stilusbiro_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, stilusbiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.stilusbiro?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Díjlovas bíró(k)</label>
                      <select 
                        multiple
                        value={createForm.dijlovasbiro_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, dijlovasbiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.dijlovasbiro?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Lovastusa bíró(k)</label>
                      <select 
                        multiple
                        value={createForm.lovastusabiro_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, lovastusabiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.lovastusabiro?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Melegítőpálya bíró(k)</label>
                      <select 
                        multiple
                        value={createForm.melegitobiro_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, melegitobiro_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.melegitobiro?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Versenyszervező(k)</label>
                      <select 
                        multiple
                        data-testid="create-director-select"
                        value={createForm.director_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, director_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.organizers?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Pályaépítő(k)</label>
                      <select 
                        multiple
                        data-testid="create-course-builder-select"
                        value={createForm.course_builder_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, course_builder_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.course_builders?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Versenyiroda</label>
                      <select 
                        multiple
                        value={createForm.versenyiroda_ids || []} 
                        onChange={(e) => setCreateForm({...createForm, versenyiroda_ids: Array.from(e.target.selectedOptions, o => o.value)})} 
                        className="w-full border border-slate-200 rounded-lg p-2 text-slate-800 bg-white min-h-[80px]"
                      >
                        {roleUsers.versenyiroda?.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Ctrl+klikk több személy kiválasztásához</p>
                </div>

                {/* Nevezési díj szekció */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <input 
                      type="checkbox" 
                      id="entry-fee-enabled-create"
                      data-testid="entry-fee-enabled-create"
                      checked={createForm.entry_fee_enabled || false}
                      onChange={(e) => setCreateForm({...createForm, entry_fee_enabled: e.target.checked})}
                      className="w-5 h-5 rounded border-slate-300"
                    />
                    <label htmlFor="entry-fee-enabled-create" className="font-semibold text-slate-800 cursor-pointer">Nevezési díj</label>
                  </div>
                  
                  {createForm.entry_fee_enabled && (
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                      <p className="text-sm text-emerald-700 mb-4">
                        Állítsd be a sávos díjazást: más ár az első néhány startnál, kedvezményes ár több start esetén.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Első sáv határa (start)</label>
                          <input 
                            type="number" 
                            min="1"
                            data-testid="entry-fee-tier1-limit-create"
                            value={createForm.entry_fee_tier1_limit || 2}
                            onChange={(e) => setCreateForm({...createForm, entry_fee_tier1_limit: parseInt(e.target.value) || 2})}
                            className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                            placeholder="pl. 2"
                          />
                          <p className="text-xs text-slate-500 mt-1">Ennyi startig érvényes az első sáv díja</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Első sáv díja (Ft/start)</label>
                          <input 
                            type="number" 
                            min="0"
                            step="100"
                            data-testid="entry-fee-tier1-price-create"
                            value={createForm.entry_fee_tier1_price || 3000}
                            onChange={(e) => setCreateForm({...createForm, entry_fee_tier1_price: parseInt(e.target.value) || 0})}
                            className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                            placeholder="pl. 3000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Kedvezményes díj (Ft/start)</label>
                          <input 
                            type="number" 
                            min="0"
                            step="100"
                            data-testid="entry-fee-tier2-price-create"
                            value={createForm.entry_fee_tier2_price || 2500}
                            onChange={(e) => setCreateForm({...createForm, entry_fee_tier2_price: parseInt(e.target.value) || 0})}
                            className="w-full border border-emerald-300 rounded-lg p-3 text-slate-800 bg-white"
                            placeholder="pl. 2500"
                          />
                          <p className="text-xs text-slate-500 mt-1">{createForm.entry_fee_tier1_limit || 2}. start felett</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                        <p className="text-sm text-slate-700">
                          <strong>Példa:</strong> 1-{createForm.entry_fee_tier1_limit || 2} start: <span className="text-emerald-600 font-semibold">{(createForm.entry_fee_tier1_price || 3000).toLocaleString('hu-HU')} Ft/start</span>, {(createForm.entry_fee_tier1_limit || 2) + 1}. starttól: <span className="text-emerald-600 font-semibold">{(createForm.entry_fee_tier2_price || 2500).toLocaleString('hu-HU')} Ft/start</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-800">Versenyszámok</h4>
                    <Button variant="outline" size="sm" onClick={addEventForm}><Plus size={16} className="mr-1" />Hozzáad</Button>
                  </div>

                  {eventForms.map((ef, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-xl p-4 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-700">#{idx + 1}</span>
                        <button onClick={() => removeEventForm(idx)} className="text-red-500 hover:text-red-700"><X size={20} /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input type="text" placeholder="Versenyszám neve" value={ef.name} onChange={(e) => updateEventForm(idx, 'name', e.target.value)} className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                        <select value={ef.category} onChange={(e) => updateEventForm(idx, 'category', e.target.value)} className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white">
                          {CATEGORY_ORDER.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                        <select value={ef.judging_type} onChange={(e) => updateEventForm(idx, 'judging_type', e.target.value)} className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white">
                          {Object.entries(JUDGING_TYPES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                        <input type="text" placeholder="Magasság (pl. 100 cm)" value={ef.height} onChange={(e) => updateEventForm(idx, 'height', e.target.value)} className="border border-slate-200 rounded-lg p-3 text-slate-800 bg-white" />
                      </div>
                      {/* Conditional fields based on judging type */}
                      {ef.judging_type === 'alapido' && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <label className="block text-sm font-medium text-amber-800 mb-1">Alapidő (másodperc) *</label>
                          <input 
                            type="number" 
                            value={ef.base_time || ""} 
                            onChange={(e) => updateEventForm(idx, 'base_time', parseInt(e.target.value) || null)} 
                            className="w-full border border-amber-300 rounded-lg p-2 text-slate-800 bg-white" 
                            placeholder="Kötelező! pl. 65" 
                            required
                          />
                          <p className="text-xs text-amber-600 mt-1">+1 hibapont minden megkezdett 2 mp túllépés után</p>
                        </div>
                      )}
                      {ef.judging_type === 'dijlovaglas' && (
                        <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                          <label className="block text-sm font-medium text-indigo-800 mb-1">Maximum pontszám *</label>
                          <input 
                            type="number" 
                            value={ef.max_points || ""} 
                            onChange={(e) => updateEventForm(idx, 'max_points', parseInt(e.target.value) || null)} 
                            className="w-full border border-indigo-300 rounded-lg p-2 text-slate-800 bg-white" 
                            placeholder="Kötelező! pl. 180" 
                            required
                          />
                          <p className="text-xs text-indigo-600 mt-1">A százalék automatikusan számolódik</p>
                        </div>
                      )}
                      {ef.judging_type === 'idealis_ido' && (
                        <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                          <label className="block text-sm font-medium text-teal-800 mb-1">Ideális idő (másodperc) *</label>
                          <input 
                            type="number" 
                            value={ef.ideal_time || ef.base_time || ""} 
                            onChange={(e) => updateEventForm(idx, 'ideal_time', parseInt(e.target.value) || null)} 
                            className="w-full border border-teal-300 rounded-lg p-2 text-slate-800 bg-white" 
                            placeholder="Kötelező! pl. 90" 
                            required
                          />
                          <p className="text-xs text-teal-600 mt-1">Az ideális időhöz legközelebbi idő nyer</p>
                        </div>
                      )}
                      {(ef.judging_type === 'stilus' || ef.judging_type === 'caprilli' || ef.judging_type === 'pontgyujto' || ef.judging_type === 'fokozatos') && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <label className="block text-sm font-medium text-purple-800 mb-1">Maximum pontszám (opcionális)</label>
                          <input 
                            type="number" 
                            value={ef.max_points || ""} 
                            onChange={(e) => updateEventForm(idx, 'max_points', parseInt(e.target.value) || null)} 
                            className="w-full border border-purple-300 rounded-lg p-2 text-slate-800 bg-white" 
                            placeholder="pl. 100" 
                          />
                          <p className="text-xs text-purple-600 mt-1">
                            {ef.judging_type === 'stilus' && "Ló-lovas harmónia, ülés, segítségadás értékelése"}
                            {ef.judging_type === 'caprilli' && "Ló idomítottsága, lovas ülése pontozása"}
                            {ef.judging_type === 'pontgyujto' && "Akadályokért pontok járnak, legtöbb pont nyer"}
                            {ef.judging_type === 'fokozatos' && "Akadályok egyre több pontot érnek"}
                          </p>
                        </div>
                      )}
                      {ef.judging_type === 'vadaszugratas' && (
                        <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <p className="text-sm text-orange-800">
                            <strong>C-szabály:</strong> Hibák másodpercekké válnak (+4 mp/hiba), összidő dönt.
                          </p>
                        </div>
                      )}
                      {ef.judging_type === 'hunter' && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-800">
                            <strong>LAUSZ Hunter:</strong> Stílus + akadályteljesítés kombinált értékelése.
                          </p>
                        </div>
                      )}
                      {ef.judging_type === 'tajekozdasi' && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>Tájékozódási:</strong> Útvonal követése térképpel, pontosság és idő alapján.
                          </p>
                        </div>
                      )}
                      {/* Show judging type description */}
                      {ef.judging_type && JUDGING_DESCRIPTIONS[ef.judging_type] && !['alapido', 'dijlovaglas', 'idealis_ido', 'stilus', 'caprilli', 'pontgyujto', 'fokozatos', 'vadaszugratas', 'hunter', 'tajekozdasi'].includes(ef.judging_type) && (
                        <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                          <p className="text-xs text-slate-600">{JUDGING_DESCRIPTIONS[ef.judging_type]}</p>
                        </div>
                      )}
                      
                      {/* Ranglista pontszámítás beállítás */}
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={ef.counts_for_ranking !== false} 
                            onChange={(e) => updateEventForm(idx, 'counts_for_ranking', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">Számít a ranglistába</span>
                        </label>
                        <p className="text-xs text-slate-500 mt-1 ml-6">
                          Ha kikapcsolod, ez a versenyszám nem ad ranglista pontot
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-6 border-t mt-6">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">Mégse</Button>
                <Button data-testid="submit-create-competition-btn" onClick={handleCreateCompetition} disabled={!createForm.name || !createForm.date} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Verseny létrehozása</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <UserManagementModal />
      <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} onLoginSuccess={(userData, userToken) => { setUser(userData); setToken(userToken); localStorage.setItem("comp_token", userToken); setShowAuthModal(false); }} />
    </div>
  );
}
